// @ts-check
// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { LANAC_DEBUG } from "./00-boot.jsx";
import { texteRefuse } from "./01-taxonomy-cities.jsx";
import { isUUID, listingFromDB, listingToDB } from "./05-ui-atoms-theme.jsx";
import { aggregateMediaModeration, applyMediaModerationStatus, borneSignal, ensureStoredListingImg, ensureStoredListingPhotos, ensureStoredListingVideo, ligneAnnonceSansOctets, ouvrirEnvoi, r2KeyOf, takeUploadVerdict } from "./06-images-upload.jsx";
import { lanacSupabaseClient } from "./08-supabase-client.jsx";
// === TRUST AUTO-APPROVE ======================================================================
// Permet UNIQUEMENT un upgrade pending → active pour un vendeur établi (trust élevé) dont les images
// ont été RÉELLEMENT vérifiées propres par AWS. Ne crée jamais de rejet ni de pending, ne pénalise
// jamais un nouvel utilisateur (trust bas → renvoie le statut tel quel = comportement actuel).
// Gated par TRUST_AUTOAPPROVE.enabled (OFF au départ). Les blocages durs gagnent toujours.
const TRUST_AUTOAPPROVE={enabled:true,minTrust:80,scoreCeiling:62,highTrust:90,highScoreCeiling:70,burstRelaxTo:5};
const trustAutoApproveStatus=({status,mod,mediaModeration,owner,burstNeedsReview,mediaAuditSuspicious,hasOpenReports})=>{
  if(!TRUST_AUTOAPPROVE.enabled) return status;          // interrupteur
  if(status!=="pending") return status;                  // upgrade-only : ne touche jamais active/rejected
  if(!mod) return status;
  // Blocages durs — JAMAIS contournés par le trust :
  if(mod.decision==="rejected") return status;
  if(mod.checks?.hardReject) return status;              // mots interdits durs / produit interdit
  if(mod.checks?.stockPhotoReview) return status;        // photo stock / screenshot / réutilisée / <2 photos
  if(mod.checks?.emailPendingReview) return status;      // email non vérifié
  if(mediaAuditSuspicious) return status;                // mediaOriginalityReview suspect
  if(owner?.blocked) return status;                      // compte bloqué
  if(hasOpenReports) return status;                      // signalement ouvert
  // AWS doit avoir vérifié TOUTES les images et les avoir jugées propres (off/skipped/unknown/partiel → non) :
  if(!(mediaModeration&&mediaModeration.checkedClean===true)) return status;
  // Trust (absent / bas → pas d'upgrade ; nouvel utilisateur protégé, comportement inchangé) :
  const trust=Number.isFinite(owner?.trustScore)?owner.trustScore:null;
  if(trust==null||trust<TRUST_AUTOAPPROVE.minTrust) return status;
  const high=trust>=TRUST_AUTOAPPROVE.highTrust;
  if(burstNeedsReview&&!high) return status;             // burst relâché uniquement pour high-trust
  const ceiling=high?TRUST_AUTOAPPROVE.highScoreCeiling:TRUST_AUTOAPPROVE.scoreCeiling;
  if(Number(mod.score||0)>=ceiling) return status;       // au-dessus du plafond → reste en review
  return "active";                                        // upgrade pending → active
};
// ── D5b — Le boost d'annonce est une capacité SERVEUR (migration 20260716200000) ──────────────
// grant_listing_boost() est la SEULE voie d'activation ; la garde protect_listing_boost annule tout
// boost actif écrit hors RPC. Ces helpers traduisent une INTENTION client en appel RPC, en biaisant
// volontairement vers « dans le doute, on N'octroie PAS » → jamais un crédit consommé par erreur.
const boostGrantKind=b=>{const k=b&&b.duration;return ["today","24h","3d","7d","custom"].includes(k)?k:null;};
// Intention réelle d'activer/prolonger, distincte d'un boost_sale DÉRIVÉ (endsAt recalculé à chaque
// enregistrement côté client → ne signale rien). endsAt n'est comparé QUE pour une durée custom :
// c'est ce qui empêche l'édition d'un titre d'annonce boostée de consommer un crédit.
const boostIntentChanged=(nb,ob)=>{
  if(!nb||nb.active!==true) return false;                                   // pas une activation
  if(!ob||ob.active!==true) return true;                                    // activation depuis inactif
  if((ob.duration||null)!==(nb.duration||null)) return true;               // durée changée
  if(Number(ob.discountPrice||0)!==Number(nb.discountPrice||0)) return true;// prix changé
  if(nb.duration==="custom"&&Number(ob.endsAt||0)!==Number(nb.endsAt||0)) return true; // date custom déplacée
  return false;                                                             // rien de neuf → pas d'octroi
};
// Exécute l'intention via la RPC. Renvoie le boost_sale AUTORITAIRE (relu en base — endsAt plafonné 7 j
// côté serveur) en cas de succès, {error} sinon, {skipped} si aucune intention. N'échoue JAMAIS l'écriture
// de l'annonce : l'appelant a déjà persisté la ligne, l'octroi est un effet de bord best-effort.
const grantBoostIfIntended=async(client,listingId,newBoost,oldBoost)=>{
  if(!boostIntentChanged(newBoost,oldBoost)) return {skipped:true};
  const kind=boostGrantKind(newBoost);
  if(!kind) return {skipped:true};
  try{
    const {error}=await client.rpc("grant_listing_boost",{
      p_listing_id:listingId,p_kind:kind,
      p_discount_price:Number(newBoost.discountPrice||0),
      p_custom_end:kind==="custom"?(Math.trunc(Number(newBoost.endsAt||0))||null):null,
      p_available_today:!!newBoost.availableToday,
    });
    if(error) return {error};
    const {data}=await client.from("listings").select("boost_sale").eq("id",listingId).single();
    return {boostSale:data?.boost_sale||null};
  }catch(e){return {error:e};}
};
//  C7 · T28 lot 2 — SI UN MÉDIA N'A PAS PU PARTIR, ON N'ÉCRIT PAS L'ANNONCE AMPUTÉE : publier sans lui donnerait une
//   annonce muette, et le membre n'apprendrait jamais pourquoi. F1 (29/09/2026) : le refus se lit sur l'ARDOISE de CET
//   envoi (jamais une case partagée) ; le serveur donne le CODE et le CHIFFRE (secondes), l'écran les dit dans la langue
//   de la personne (listingMediaRefusalText) ; et une ligne qui embarquerait ses octets n'est pas envoyée.
const erreurDuRefus=r=>({message:r.phrase||r.code||"upload refusé",code:r.code,phrase:r.phrase,secondes:r.secondes});
const ERREUR_PAS_UNE_ADRESSE={message:"listing media is not an address",code:"not_address",phrase:"",secondes:0};
//  ⭐ F1 étape 2 (29/09/2026) — UNE PUBLICATION NE PART QU'UNE FOIS (idempotente PAR CONSTRUCTION, comme l'outbox M2). La
//   file d'envoi passe une CLÉ tirée une fois (`cle` → `publish_key`) et son ARDOISE (ce qui est déjà parti). Un REJEU relit
//   d'abord si l'annonce est déjà née — une coupure APRÈS l'écriture perd la réponse, pas l'annonce — et ne renvoie rien ;
//   l'index unique (auteur, clé) refuse une seconde naissance (23505 nommé) : on relit alors la première, c'est un SUCCÈS.
//   ⭐ DEUX « DÉJÀ NÉE » DISTINCTES, nommées par `dejaNee` : « relue » (l'essai qui l'a écrite est MORT avec sa réponse : ses
//   suites — notifications, alertes — n'ont jamais eu lieu, c'est à nous de les jouer) et « concurrente » (un autre essai
//   VIVANT l'a écrite pendant le nôtre : ses suites sont les siennes ; les rejouer les doublerait).
//   ⚠️ UNE RELECTURE QUI ÉCHOUE N'EST PAS UNE ABSENCE : si la base ne répond pas, on n'envoie RIEN à l'aveugle (photos
//   renvoyées, écriture refusée par l'index, suites perdues) — c'est une PANNE, le prochain essai relira. Et l'erreur porte
//   son STATUT HTTP : une page de passerelle (502, 503, 520…) n'a pas de code, c'est le statut qui dit la panne.
//  ⭐ F1 étape 3 (29/09/2026) — LA RELECTURE PASSE PAR LA COMMANDE `etat_de_ma_publication` (migration 20260929194912) : en UN
//   aller-retour, l'état de SA publication — vivante (avec sa ligne), SUPPRIMÉE (le registre des clés s'en souvient : elle ne
//   renaît plus), ou absente. Bornée (20 s) et abandonnable (le signal de l'envoi : la déconnexion) : une relecture qui cale
//   est une panne, jamais une attente sans fin.
const INDEX_CLE_PUBLICATION="listings_owner_publish_key_uniq";
const RELECTURE_MAX_MS=20000, ECRITURE_MAX_MS=30000;
const ERREUR_PUBLICATION_SUPPRIMEE={message:"publication_supprimee",code:"publication_supprimee",phrase:"",secondes:0};
//  Une requête coupée par SA borne est NOMMÉE (postgrest-js la rend en `AbortError: …` sans code, statut 0 — lu dans le
//   vendor) : l'abandon (la déconnexion) ou le temps dépassé — deux pannes, jamais un refus.
const erreurCoupee=parent=>parent&&parent.aborted?{code:"abandon",message:"abandon"}:{code:"hors_delai",message:"hors délai"};
const ligneDejaNee=async(client,ownerId,cle,parent=null)=>{
  const b=borneSignal(parent,RELECTURE_MAX_MS);
  try{
    let q=client.rpc("etat_de_ma_publication",{p_cle:cle});
    if(q&&typeof q.abortSignal==="function") q=q.abortSignal(b.signal);
    const {data,error,status}=await q;
    if(error) return {data:null,supprimee:false,error:b.signal.aborted?erreurCoupee(parent):{...error,status}};
    return {data:data?.etat==="vivante"&&data.annonce?data.annonce:null,supprimee:data?.etat==="supprimee",error:null};
  }catch(e){return {data:null,supprimee:false,error:b.signal.aborted?erreurCoupee(parent):{message:String(e?.message||e),code:""}};}
  finally{b.fin();}
};
//  Née, SUPPRIMÉE, absente, ou inconnue (la base ne répond pas) — ce que demande un GESTE de la file avant d'agir (« Reprendre »,
//   « Supprimer ») : une réponse perdue peut avoir laissé une annonce EN LIGNE derrière un envoi « en attente ».
const etatDeNaissance=async(ownerId,cle)=>{
  const client=lanacSupabaseClient();
  if(!client||!ownerId||!cle) return "inconnue";
  try{const lu=await ligneDejaNee(client,ownerId,cle);return lu.error?"inconnue":lu.data?"nee":lu.supprimee?"supprimee":"absente";}catch(e){return "inconnue";}
};
//  ⭐ CE QUI SUIT L'ÉCRITURE EST LE MÊME, que l'annonce vienne de naître ou qu'elle soit DÉJÀ NÉE (rejeu après une réponse
//   perdue : l'écriture a eu lieu, ses SUITES jamais). Le boost voulu est octroyé s'il ne l'est pas déjà (l'INSERT le
//   nullifie : non nul = déjà octroyé) ; le verdict image est porté — sans lui, une photo signalée publierait « propre » :
//   l'administration ne serait pas prévenue et les alertes partiraient pour une annonce en attente. Une naissance CONCURRENTE
//   n'octroie rien (`octroyer:false`) : l'essai vivant qui l'a écrite octroie le sien — deux octrois consommeraient deux crédits.
const suitesDeNaissance=async(client,itm,data,{octroyer=true}={})=>{
  const boostIntent=(itm.boostSale&&itm.boostSale.active===true)?itm.boostSale:null;
  let boostRow=data.boost_sale||null;
  if(octroyer&&boostIntent&&!boostRow){
    const g=await grantBoostIfIntended(client,data.id,boostIntent,null);
    if(g.error) console.error("[LANAC] boost grant on insert failed:",g.error.message||g.error);
    else boostRow=g.boostSale||null; // boost_sale autoritaire, ou null si quota atteint → annonce publiée sans boost
  }
  const fromDb=listingFromDB(boostIntent?{...data,boost_sale:boostRow}:data);
  // listingFromDB réinitialise moderationFlags/Decision (non persistés) ; on préserve le verdict image
  // calculé ci-dessus pour que l'admin voie le motif et que le handler affiche le bon message.
  // Verdict bloquant (flagged/unknown) → on préserve flags+decision pour l'admin. Verdict "checkedClean"
  // (image propre vérifiée) → on ne porte QUE mediaModeration (lu par le trust auto-approve), sans
  // toucher moderationFlags/Decision → aucun effet de bord sur l'affichage admin.
  const _mmod=itm.mediaModeration;
  const mediaPatch=_mmod?((_mmod.flagged||_mmod.unknown)?{mediaModeration:_mmod,moderationFlags:itm.moderationFlags||[],moderationDecision:itm.moderationDecision}:{mediaModeration:_mmod}):{};
  return {listing:{...itm,...fromDb,...mediaPatch},error:null};
};
//  L'annonce déjà née, telle que la première écriture l'a vue : les photos ne repartent pas, leurs verdicts sont lus dans
//   l'ARDOISE (ce que la file a gardé de l'envoi qui a écrit), une photo de la ligne à la fois — la même agrégation stricte
//   qu'à la naissance (une seule photo sans verdict propre : pas de « vérifié propre »). Les canaux Blob ne sortent pas.
//   ⚠️ La ligne porte des CLÉS R2 (listingToDB → r2KeyOf) quand l'ardoise porte des ADRESSES : les deux côtés sont lus par
//   leur clé (sinon aucun verdict ne se retrouve, et une photo signalée publierait « propre »). La mémoire de session est
//   rendue au passage.
const itemDejaNe=(item,envoi,data)=>{
  const {photoFiles:_pf,videoFile:_vf,...base}=item;
  const cleDe=u=>r2KeyOf(u)||u;
  const faits=Object.values(envoi?.faits?.photos||{}).filter(f=>f&&f.url);
  const parCle=new Map(faits.map(f=>[cleDe(f.url),takeUploadVerdict(f.url)||f.verdict||null]));
  const verdicts=(Array.isArray(data.photos)?data.photos:[]).map(p=>parCle.get(cleDe(p))||null);
  const mediaModeration=aggregateMediaModeration(verdicts);
  return applyMediaModerationStatus(mediaModeration?{...base,mediaModeration}:base);
};
const persistListingInsert=async(item,{envoi:envoiDonne=null,cle=null,dejaTente=false}={})=>{
  const client=lanacSupabaseClient();
  if(!client) return {listing:null,error:{message:"Supabase client unavailable"}};
  try{
    const envoi=envoiDonne||ouvrirEnvoi();
    if(cle&&dejaTente){
      const lu=await ligneDejaNee(client,item.ownerId,cle,envoi.signal||null);
      if(lu.error) return {listing:null,error:lu.error};
      //  SUPPRIMÉE depuis sa naissance (réponse perdue, puis la personne l'a retirée) : elle ne renaît pas — la file le dit.
      if(lu.supprimee) return {listing:null,error:ERREUR_PUBLICATION_SUPPRIMEE};
      if(lu.data) return {...await suitesDeNaissance(client,itemDejaNe(item,envoi,lu.data),lu.data),dejaNee:"relue"};
    }
    let itm=await ensureStoredListingPhotos(item,envoi);
    itm=await ensureStoredListingVideo(itm,envoi);
    if(envoi.refus)return {listing:null,error:erreurDuRefus(envoi.refus)};
    //  Abandonné entre deux étapes (la déconnexion) : rien n'est écrit.
    if(envoi.signal&&envoi.signal.aborted) return {listing:null,error:{code:"abandon",message:"abandon"}};
    itm=applyMediaModerationStatus(itm); // override éventuel du statut selon le verdict image
    // D5b — On retire un boost ACTIF du payload (le guard le nullifie de toute façon à l'INSERT) puis
    // on l'octroie via la RPC APRÈS insert, quand la ligne existe et que la RPC peut lire son prix réel.
    const boostIntent=(itm.boostSale&&itm.boostSale.active===true)?itm.boostSale:null;
    const payload={...listingToDB(boostIntent?{...itm,boostSale:null}:itm),owner_id:item.ownerId,...(cle?{publish_key:cle}:{})};
    if(!ligneAnnonceSansOctets(payload))return {listing:null,error:ERREUR_PAS_UNE_ADRESSE};
    //  L'écriture PART : l'ardoise le retient (un geste de la file saura qu'une annonce a pu naître derrière un « en attente »).
    if(envoi.faits) envoi.faits.insertTente=true;
    //  L'écriture est BORNÉE (30 s) et abandonnable : l'abandonner est SÛR — la clé rend le rejeu idempotent (il relit).
    const b=borneSignal(envoi.signal||null,ECRITURE_MAX_MS);
    let ecrit;
    try{
      let q=client.from("listings").insert(payload).select("*");
      if(q&&typeof q.abortSignal==="function") q=q.abortSignal(b.signal);
      ecrit=await q.single();
    }finally{b.fin();}
    const {data,error,status}=ecrit;
    //  Coupée (borne ou déconnexion) : elle a PU naître côté base — l'ardoise dit `insertTente`, le rejeu relira.
    if(error&&b.signal.aborted) return {listing:null,error:erreurCoupee(envoi.signal)};
    if(error){
      //  La base refuse une clé dont l'annonce est partie (le registre) : nommé, jamais un « refus » à dire comme tel.
      if(cle&&error.code==="P0001"&&/publication_supprimee/.test(String(error.message||""))) return {listing:null,error:ERREUR_PUBLICATION_SUPPRIMEE};
      if(cle&&error.code==="23505"&&String(error.message||"").includes(INDEX_CLE_PUBLICATION)){
        const lu=await ligneDejaNee(client,item.ownerId,cle,envoi.signal||null);
        if(lu.supprimee) return {listing:null,error:ERREUR_PUBLICATION_SUPPRIMEE};
        if(lu.data)return {...await suitesDeNaissance(client,itm,lu.data,{octroyer:false}),dejaNee:"concurrente"};
        //  L'index l'a DIT née, la relecture n'a pas pu la rendre : jamais un « refus » — une panne, le prochain essai relira.
        return {listing:null,error:lu.error||{code:"deja_nee_illisible",message:"annonce née, relecture impossible"}};
      }
      //  (B-MOTS) un texte refusé par la base est une RÈGLE dite à l'écran, pas une panne : la console reste propre
      if(texteRefuse(error)===null) console.error("[LANAC] listing insert error:",error.message,error.code,payload);
      else LANAC_DEBUG&&console.info("[LANAC] listing insert : texte refusé par la base");
      return {listing:null,error:{...error,status}};
    }
    return await suitesDeNaissance(client,itm,data);
  }catch(error){
    console.error("[LANAC] listing insert exception:",error?.message||error);
    return {listing:null,error};
  }
};
const persistListingUpdate=async(id,patch,label="listing update")=>{
  const client=lanacSupabaseClient();
  if(!client||!id) return {error:null};
  // Si la mise à jour contient des photos base64 (édition d'annonce) → les stocker compressées sur Storage
  //  F1 : l'ardoise est celle de CET envoi — une écriture sans média (statut, réservation, rappel) n'en lit aucun autre.
  const envoi=ouvrirEnvoi();
  let workPatch=patch;
  if(Array.isArray(patch?.photos)&&patch.photos.some(p=>typeof p==="string"&&p.startsWith("data:image"))){
    workPatch=await ensureStoredListingPhotos({...workPatch,id},envoi);
  }else if(typeof patch?.img==="string"&&patch.img.startsWith("data:image")){
    workPatch={...workPatch,img:await ensureStoredListingImg(patch.img,id,envoi)};
  }
  // Si une NOUVELLE vidéo est ajoutée à l'édition → l'uploader sur R2 (vraie URL au lieu du blob:)
  if(patch?.videoFile||(patch?.video&&patch.video._file)){
    workPatch=await ensureStoredListingVideo({...workPatch,id},envoi);
  }
  //  C7 · T28 lot 2 — même règle qu'à la publication : un média qui n'a pas pu partir n'est pas un média
  //   perdu en silence. On n'écrit pas la modification amputée, et on dit pourquoi.
  if(envoi.refus)return {error:erreurDuRefus(envoi.refus)};
  // Si de nouvelles photos viennent d'être uploadées et qu'un verdict image est bloquant → statut
  // forcé (validation/refus). No-op si aucune nouvelle photo ou modération OFF (boost = aucun upload).
  workPatch=applyMediaModerationStatus(workPatch);
  // D5b — Boost = capacité SERVEUR. Une activation/prolongation (active:true) NE s'écrit PAS en direct
  // (la garde l'annulerait) : on la retire du payload et on l'exécute via grant_listing_boost APRÈS
  // l'update (pour que la RPC lise le prix à jour). L'ARRÊT d'un boost (active:false) reste une écriture
  // directe, autorisée par la garde. L'intention est comparée à l'état SERVEUR (pas au patch client),
  // et grantBoostIfIntended ignore la dérive de endsAt → une simple édition de titre ne consomme rien.
  const boostIntent=(workPatch&&workPatch.boostSale&&workPatch.boostSale.active===true)?workPatch.boostSale:null;
  let oldBoost=null;
  if(boostIntent){
    try{const {data:cur}=await client.from("listings").select("boost_sale").eq("id",id).single();oldBoost=cur?.boost_sale||null;}catch(_){}
    workPatch={...workPatch,boostSale:undefined}; // le serveur est propriétaire du boost actif
  }
  const payload=listingToDB(workPatch);
  if(!ligneAnnonceSansOctets(payload))return {error:ERREUR_PAS_UNE_ADRESSE};
  const grant=async()=>{if(!boostIntent)return;const g=await grantBoostIfIntended(client,id,boostIntent,oldBoost);if(g.error)console.error(`[LANAC] boost grant on update failed:`,g.error.message||g.error);};
  if(!Object.keys(payload).length){await grant();return {error:null};}
  try{
    const res=await client.from("listings").update(payload).eq("id",id);
    if(res?.error){console.error(`[LANAC] ${label} error:`,res.error.message,res.error.code,payload);return res;}
    await grant();
    return res;
  }catch(error){console.error(`[LANAC] ${label} exception:`,error?.message||error,payload);return {error};}
};
// -- SUPABASE CONVO/MESSAGE MAPPERS -------------------------------------------
const messageFromDB=row=>!row?null:({
  id:row.id,
  convoId:row.convo_id||null, // M5b : requis pour le non-lu par watermark (lookup read_seq par fil)
  from:row.system?"system":(row.from_id||"system"),
  //  (lot D · D5, 30/10/2026, revue APRÈS B1) la VRAIE marque « système » : `from` vaut aussi "system" pour le message d'un compte EFFACÉ (from_id nul,
  //   system faux) — messageUpdateToDB ne doit traiter en carte du système que ce que la base tient pour telle.
  system:!!row.system,
  text:row.text||"",
  ts:Number(row.ts||0)||new Date(row.created_at||0).getTime(),
  read:!!row.read,
  kind:row.kind||"chat",
  status:row.status||"sent",
  data:row.data||null,
  image:row.image||null,
  //  (lot D · D6-b, 31/10/2026, revue AVANT M2) une lecture ALLÉGÉE (l'aperçu, l'inbox) ne porte pas reply_to / removed_at : la clé est alors
  //   ABSENTE (jamais « null » ni « false ») et mergeConvoMessageLists garde la valeur déjà connue — une réponse citée ne se perd plus, un
  //   message retiré ne redevient plus une bulle vide (la réouverture par delta ne relit plus le fil entier pour le réparer).
  ...("reply_to" in row?{replyTo:row.reply_to||null}:{}),
  reservationAction:row.data?.reservationAction||null,
  reactions:Array.isArray(row.data?._ui?.reactions)?row.data._ui.reactions:[],
  editedAt:row.data?._ui?.editedAt||null,
  readAt:row.data?._ui?.readAt||null,
  pinned:!!(row.data?._ui?.pinned),
  // D2 : la colonne SERVEUR `removed_at` fait foi — et SEULE (C2a-1-ter, 15/10) : le repli sur `_ui.removed` (les
  //  messages retirés avant la bascule — il n'en reste aucun en base) laissait l'une ou l'autre partie faire PARAÎTRE
  //  retiré le message de l'autre, hors de la commande (remove_message : l'auteur, quinze minutes, jamais un message signalé).
  ...("removed_at" in row?{removed:!!row.removed_at,removedAt:row.removed_at?Date.parse(row.removed_at):null}:{}),
  seq:(row.seq!=null&&Number.isFinite(Number(row.seq)))?Number(row.seq):null, // M1/M3 : position serveur (null=optimiste ; garde !=null car Number(null)===0)
  // A3 completeness (levier OCTETS Phase 2) : `_p` marque un payload APERÇU (texte tronqué, voie inbox
  // lanac:user). Absent partout aujourd'hui → _preview=false → machine de complétude = strict no-op.
  _preview:!!row._p,
});
const messageDataToDB=m=>{
  const raw=m.data&&typeof m.data==="object"&&!Array.isArray(m.data)?{...m.data}:m.data;
  // Ne jamais stocker de base64 en DB (itemPhoto, listingPhoto) — egress ×10 si présent
  const base=raw&&typeof raw==="object"?(()=>{const b={...raw};if(typeof b.itemPhoto==="string"&&b.itemPhoto.startsWith("data:"))b.itemPhoto=null;if(typeof b.listingPhoto==="string"&&b.listingPhoto.startsWith("data:"))b.listingPhoto=null;return b;})():raw;
  const ui={
    reactions:m.reactions||[],
    editedAt:m.editedAt||null,
    readAt:m.readAt||null,
    pinned:!!m.pinned,
  };
  //  (C2a-1-ter) le retrait n'est plus écrit dans `_ui` : la colonne `removed_at`, posée par la commande, est la seule à le dire.
  const hasUi=ui.reactions.length||ui.editedAt||ui.readAt||ui.pinned;
  let out = base&&typeof base==="object" ? (hasUi?{...base,_ui:ui}:base._ui?{...base,_ui:ui}:base)
                                          : (hasUi?{_ui:ui}:base||null);
  // reservationAction : champ React autoritaire (pas une colonne DB). On le replie dans
  // data pour qu'il persiste + se synchronise en realtime (comme autoClosedReason)
  // -> fin des boutons d'action fantomes apres reload / chez l'autre partie.
  if(out&&typeof out==="object"&&!Array.isArray(out)){
    if(m.reservationAction) out.reservationAction=m.reservationAction;
    else delete out.reservationAction; // efface une valeur perimee si la carte est rouverte
  } else if(m.reservationAction){
    out={reservationAction:m.reservationAction};
  }
  return out;
};
const messageToDB=(m,convoId)=>({
  id:m.id,
  convo_id:convoId,
  from_id:m.from==="system"?null:m.from,
  text:m.text||"",
  ts:m.ts||Date.now(),
  system:m.from==="system",
  //  (lot D · D6-b, 31/10/2026) `read` n'est plus écrit à l'insertion : la colonne vaut `false` par défaut (NOT NULL) — la même valeur stockée.
  kind:m.kind||"chat",
  status:m.status||"sent",
  data:messageDataToDB(m),
  image:m.image||null,
  reply_to:m.replyTo||null,
});
//  B7 (14/10/2026) : le STATUT d'une offre, d'une demande de réservation ou de visite ne s'écrit que par sa COMMANDE (la base
//   neutralise tout autre changement) — une mise à jour de ces cartes n'en porte donc plus, ni le type (que la base fige) :
//   une copie périmée ne réécrit plus la décision de l'autre partie, et rien n'est refusé ni inscrit au journal pour rien.
const messageUpdateToDB=m=>{
  const row={
    text:m.text||"",
    ts:m.ts||Date.now(),
    //  (lot D · D5-c, 30/10/2026) `read` n'est plus écrit par une mise à jour : la lecture passe par mark_convo_read (read_seq) — préalable à D4b,
    //   qui retirera UPDATE(read) aux membres (sinon chaque réaction recevrait un 42501). L'insertion l'écrit encore (faux par défaut ; D6).
    kind:m.kind||"chat",
    status:m.status||"sent",
    data:messageDataToDB(m),
    image:m.image||null,
    reply_to:m.replyTo||null,
  };
  if(m.kind==="offer"||m.kind==="reserve"||m.kind==="visit"){delete row.kind;delete row.status;}
  //  (lot C · C2d-1, 16/10/2026) … et le TAMPON de clôture d'une réservation, d'une visite, d'une offre, d'une clôture ou d'une
  //   carte « ne convient pas » ne s'écrit que par sa COMMANDE (C2b, C2c) : une mise à jour ne le porte plus, ni le STATUT d'une
  //   clôture ou d'une carte « ne convient pas » (la base seule l'écrit). Réactions, épingle, lu, lieu de rendez-vous réécrivent
  //   la carte entière depuis la copie locale — qui peut porter un tampon que la base n'a pas encore (posé en mémoire avant la
  //   réponse d'une commande, gardé par le cache si l'application est tuée) : la base garde le sien, la copie ne dit plus rien.
  //  (lot C · C3-3, 18/10/2026) … et la carte d'ACCORD (« Transaction à confirmer ») : son statut et son tampon ne s'écrivent que par leurs
  //   commandes (confirm_deal, report_incident, cancel_deal_confirmation, la remise en vente) — une mise à jour depuis l'appareil n'en porte plus.
  //  (lot C · C4-2, 19/10/2026) … et les cartes « Après la visite » et « Après l'essai » : leur statut et leur tampon ne s'écrivent que par
  //   answer_post_visit — une réaction depuis une copie périmée ne remet plus « à choisir » une carte que l'autre a confirmée.
  if(["offer","reserve","visit","finalAction","itemUnsuitableConfirm","dealConfirm","postVisitDeal","postVisitReservation"].includes(m.kind)&&row.data&&typeof row.data==="object"&&!Array.isArray(row.data)){
    const _d={...row.data};delete _d.reservationAction;row.data=_d;
  }
  if(["finalAction","itemUnsuitableConfirm","dealConfirm","postVisitDeal","postVisitReservation"].includes(m.kind)){delete row.kind;delete row.status;}
  //  (lot C · C6-1, 30/10/2026) … et les cartes que la BASE SEULE écrit (l'invite de report, les journaux de décision, le lieu de rendez-vous, l'accord
  //   confirmé et son rappel, « après l'essai », « après la visite ») : la base fige leur statut et leurs données, seul `_ui` passe (guard_card_marks_by_command).
  //   La ligne ne porte donc que `_ui` (réactions, épingle, lu) — plus d'écho d'une copie périmée, moins d'octets ; sans `_ui`, aucune donnée (la base garde
  //   les siennes, et celles de l'autre).
  //  (lot D · D5, 30/10/2026) … et les cartes d'ENGAGEMENT (réservation, visite, clôture, « ne convient pas » : leurs données ne changent plus que par
  //   leurs commandes — la base les garde, seul `_ui` passe) et toute carte du SYSTÈME (sans auteur : sa clé de texte et son statut sont à la base).
  //   ⚠️ ORDRE : la migration « les cartes restantes » AVANT ce client — l'inverse effacerait leurs données (revue H1). La liste est la MÊME que la
  //   branche « données » de guard_card_marks_by_command (banc une-carte-ne-s-ecrit-plus-depuis-une-copie) ; posée ICI pour voyager avec la fonction.
  const CARTES_DONNEES_FIGEES=["reserve","visit","finalAction","itemUnsuitableConfirm"];
  if(CARTES_DE_LA_BASE.includes(m.kind)||CARTES_DONNEES_FIGEES.includes(m.kind)||m.system===true){
    delete row.kind;delete row.status;
    const ui=row.data&&typeof row.data==="object"&&!Array.isArray(row.data)?row.data._ui:undefined;
    if(ui!==undefined) row.data={_ui:ui}; else delete row.data;
  }
  return row;
};
//  C6-1 : les genres de cartes que la base seule écrit — la MÊME liste que guard_card_marks_by_command (banc une-carte-ne-s-ecrit-plus-depuis-une-copie).
const CARTES_DE_LA_BASE=Object.freeze(["reservationExtension","reserveDecision","visitDecision","offerDecision","meetingPlace","dealConfirmed","dealConfirmReminder","postVisitReservation","postVisitDeal"]);
// F5 (22/07) — SÉCURITÉ : les 6 kill-switch RPC côté client (décision, vote, deal, carton trust, transitions
// d'annonce pilotées acheteur) sont SUPPRIMÉS DÉFINITIVEMENT. Un flag localStorage ne doit jamais pouvoir
// router une écriture transactionnelle vers un fallback direct (contournement RLS/serveur), ni annuler un
// carton trust. Seuls les chemins RPC serveur-autoritaires subsistent (comportement = défaut ON antérieur,
// prod inchangée). Réapparition d'une clé `lanac:*RPC` ou d'un symbole `lanac*RPCOn` → cassée par
// tools/data-access-guard.mjs (prohibition absolue). Un vrai rollback = revert de déploiement, pas un flag client.
// Nudge « Compléter le profil » : montré une fois par appareil. Sans ça, un compte né `profile_incomplete=true`
// (ex. comptes email) re-nagguait à CHAQUE connexion (le simple close ne persistait rien). Compléter le profil
// persiste en plus profileIncomplete=false en base (updateMe) → ne réapparaît plus nulle part.
const profileNudgeSeen=id=>{try{return !!id&&!!localStorage.getItem("lanac:profileNudgeSeen:"+id);}catch(_){return false;}};
const markProfileNudgeSeen=id=>{try{if(id)localStorage.setItem("lanac:profileNudgeSeen:"+id,"1");}catch(_){}};
const persistMessageUpdate=(convoId,message)=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId||!message?.id) return Promise.resolve({error:null});
  const doUpdate=()=>client.from("messages").update(messageUpdateToDB(message)).eq("id",message.id).eq("convo_id",convoId);
  return doUpdate()
    .then(res=>{
      // 42501 = JWT expiré au moment de l'écriture : re-vérifier la session (getSession, PAS
      // refreshSession → pas de rotation du refresh token) puis réessayer une seule fois.
      if(res?.error?.code==="42501"){
        return client.auth.getSession().then(({data})=>data?.session?doUpdate():res).catch(()=>res);
      }
      return res;
    })
    .then(res=>{
      //  (B-MOTS) un texte refusé par la base est une RÈGLE (dite par l'appelant), pas une panne : la console reste propre
      if(res?.error&&texteRefuse(res.error)===null)console.error("[LANAC] message update error:",res.error.message,res.error.code);
      // M4 : diffusion de la mise à jour = SERVEUR-AUTORITATIVE (trigger broadcast_message → msg_update
      // sur lanac:convo + message sur lanac:user). Le pont client est retiré : l'UPDATE ci-dessus suffit.
      return res;
    })
    .catch(error=>{console.error("[LANAC] message update exception:",error?.message||error);return {error};});
};
const convoFromDB=row=>!row?null:({
  id:row.id,
  participants:Array.isArray(row.participants)?row.participants:[],
  refId:row.ref_id||null,
  refTitle:row.ref_title||"",
  refCat:row.ref_cat||"autres",
  refPhoto:row.ref_photo||null,
  refSellerName:row.ref_seller_name||null,
  blockedBy:Array.isArray(row.blocked_by)?row.blocked_by:[],
  messages:Array.isArray(row.messages)?row.messages.map(messageFromDB).filter(Boolean).sort((a,b)=>a.ts-b.ts):[],
  lastMessage:null,
  unreadCount:null,
  messagesLoaded:false,
  // Audit 2026-07-11 : curseur serveur du convo. Sert à RE-CHARGER le fil complet quand le serveur a
  // modifié un message EN PLACE (auto-résolution J+10 : statut incident pending→accepted) que le realtime
  // a manqué (canal coupé au moment du cron). Sans ça, un convo déjà ouvert restait figé sur l'ancien état.
  updatedAt:row.updated_at||null,
});
const convoToDB=c=>{
  // Ne jamais stocker de base64 en ref_photo — chaque conversation pèserait 2 MB d'egress
  const safeRefPhoto=(c.refPhoto&&typeof c.refPhoto==="string"&&!c.refPhoto.startsWith("data:"))?c.refPhoto:null;
  return {
    id:c.id,
    participants:c.participants||[],
    ref_id:c.refId||null,
    ref_title:c.refTitle||null,
    ref_cat:c.refCat||"autres",
    ref_photo:safeRefPhoto,
    ref_seller_name:c.refSellerName||null,
    // C7 · T12 (16/09) : titre, catégorie, photo et nom du vendeur d'un fil viennent de l'ANNONCE — la base les dérive à la
    //  création et ne laisse ensuite un appareil que compléter un champ vide avec ce que l'annonce montre (ou effacer la
    //  photo). Ces quatre valeurs sont donc un écho : les mêmes que la base, ou ignorées.
    // C7 · bloqueurs (15/09) : blocked_by n'est plus écrit par un appareil — la base le pose (set_conversation_block) ou le
    //  dérive (un fil créé hérite des blocages de la paire) ; sa garde ignore toute autre écriture.
  };
};
//  (lot D · D6-f, 31/10/2026) LES FILS EN CRÉATION — un fil NEUF s'ouvre avant que la base l'ait écrit (ses participants sont posés à
//   l'insertion, sync_conversation_participants) : un canal privé rejoint trop tôt est REFUSÉ, et Realtime ne le laisse revenir qu'au rejoin
//   suivant, ~5 s plus tard (mesuré, S182). Le fil inscrit ici la promesse de son ÉCRITURE ; ses canaux l'attendent (useConversationChannel).
//   EN MÉMOIRE seulement (un drapeau mis en cache survivrait à un rechargement) ; l'entrée meurt quand la promesse est réglée ;
//   ⚠️ PAR-UTILISATEUR ⇒ purgé dans lanacResetPerUserState. Seuls les fils NEUFS s'inscrivent (un fil existant s'ouvre sans attendre).
const lanacFilsEnCreation=new Map();
const noterCreationDuFil=(convoId,promesse)=>{
  if(!convoId||!promesse||typeof promesse.then!=="function") return;
  const p=Promise.resolve(promesse).then(()=>{},()=>{}).then(()=>{ if(lanacFilsEnCreation.get(convoId)===p) lanacFilsEnCreation.delete(convoId); });
  lanacFilsEnCreation.set(convoId,p);
};
// M4 (15/07) : le pont Broadcast client (lanacBroadcastBridge) est RETIRÉ — la diffusion temps réel est
// désormais 100 % serveur-autoritative (trigger broadcast_message → lanac:convo msg/msg_update + lanac:user).
const persistConvoAndMessage=(convo,message,opts)=>{
  const client=lanacSupabaseClient();
  if(!convo||!message) return Promise.resolve({error:null}); // args manquants (jamais en prod) : rien à écrire, PAS un échec de livraison
  // FIABILITÉ (finding Codex 2026-07-23) : SANS client Supabase, le message n'a PAS quitté l'appareil. On renvoie
  //  une ERREUR (et non {error:null}) → le flux d'envoi (useMessageDelivery.persistFailed) marque le message `failed`
  //  (badge + retry), au lieu de l'afficher faussement « envoyé ». Sans client, aucune écriture n'était de toute
  //  façon possible : les autres appelants (fire-and-forget) sont inchangés. Fondation outbox : le retry repart prêt.
  if(!client) return Promise.resolve({error:{message:"no_supabase_client",code:"NO_CLIENT"}});
  //  (D6-f) un fil NEUF (`opts.nouveau`, posé par les seuls appelants qui le créent) : ses canaux attendent l'écriture de la CONVERSATION — pas
  //   celle du message, qui la suit — et, au pire, la fin de la chaîne (réessai compris).
  let finCreation=null;
  if(opts&&opts.nouveau===true) noterCreationDuFil(convo.id,new Promise(r=>{finCreation=r;}));
  const row=messageToDB(message,convo.id);
  // M5c : à ON, on renvoie le seq attribué (assign_message_seq) → l'émetteur estampille son message
  // optimiste et le « Vu » (✓✓) peut s'allumer en LIVE dessus. À OFF : insert nu (octet-identique).
  //  (A42) UN SEUL site d'écriture du message : le chemin entier et le réessai sans la conversation le partagent.
  const ecrireLeMessage=()=>{
    const ins=client.from("messages").insert(row);
    return (typeof lanacReadWatermark!=="undefined"&&lanacReadWatermark)?ins.select("seq"):ins;
  };
  const doUpsertAndInsert=()=>
    client.from("conversations").upsert(convoToDB(convo),{onConflict:"id"}).then(({error,status})=>{
      if(!error&&finCreation) finCreation();
      if(error){
        console.error("[LANAC] conversation persist error:",error.message,error.code);
        return {error,status,etape:"conversation"}; // (A42) le statut HTTP suit l'erreur : une 502 de passerelle est une panne, pas une règle
      }
      return ecrireLeMessage();
    });
  //  ⭐ A42 — UN RÉESSAI NE RÉÉCRIT PAS LA CONVERSATION qu'un essai d'avant a déjà écrite (`opts.sansConvo`, posé par la relance
  //   quand l'échec venait de l'écriture du MESSAGE). La réécrire ne change rien en base, mais diffuse deux messages temps réel
  //   qui ne portent rien (mesuré le 09/10, A30) : le filet ne doit pas les multiplier. Sans l'option — le premier essai, le
  //   renvoi au geste, tous les autres appelants — rien ne change.
  const sansConvo=!!(opts&&opts.sansConvo===true);
  const ecrire=()=>sansConvo?Promise.resolve(ecrireLeMessage()):doUpsertAndInsert();
  const chaine=client.auth.getSession().catch(()=>({data:null})).then(()=>ecrire()).then(res=>{
    if(res?.error?.code==="42501"){
      // 42501 = JWT expiré/invalide au moment de l'écriture.
      // On utilise getSession() (PAS refreshSession()) : verrou interne Supabase + auto-refresh coordonné,
      // sans forcer de rotation du refresh token → évite le conflit de rotation (cascade "Refresh Token Not Found").
      console.warn("[LANAC] persist got 42501, re-checking session via getSession (no rotation) before retry...");
      return client.auth.getSession().then(({data})=>{
        if(data?.session){
          LANAC_DEBUG&&console.info("[LANAC] persist: session valid, retrying once...");
          return ecrire();
        }
        console.warn("[LANAC] persist: no valid session, skipping retry");
        return res;
      }).catch(err=>{
        console.warn("[LANAC] persist: getSession threw, skipping retry:",err?.message||err);
        return res;
      });
    }
    // ⭐ 23505 = LA LIGNE EXISTE DÉJÀ ⇒ SUCCÈS, pas erreur (A2, 27/07/2026).
    //  Les écritures de message de cette app sont IDEMPOTENTES PAR CONCEPTION : le sweep
    //  d'expiration (N2) forge des id DÉTERMINISTES pour que les deux parties convergent
    //  sur UNE ligne, et l'index `uniq_incident_response_open` interdit un second incident
    //  OUVERT sur le même évènement. Dans les deux cas, un conflit signifie « l'état visé
    //  est déjà en base » — l'intention est satisfaite. Journaliser une `console.error` là
    //  ferait passer une PROTECTION pour une panne (et rougirait S5, console-zéro) ; pire,
    //  l'appelant reverterait un optimiste correspondant à une ligne réellement présente.
    if(res?.error?.code==="23505"){
      LANAC_DEBUG&&console.info("[LANAC] message persist : ligne déjà présente (23505) — no-op idempotent");
      //  (lot C · C2d-4b) `deja` : la ligne existait — un appelant qui avait posé une copie à l'écran (un journal rejoué, à
      //   l'identifiant de son geste) peut la retirer : la ligne est dans l'histoire du fil.
      return {error:null,data:res.data??null,deja:true};
    }
    // C7 · bloqueurs T9 (16/09) : un fil bloqué refuse le message (P0001 « conversation_blocked ») — une RÈGLE, pas une panne :
    //  l'écran le dit sur la ligne du message (useMessageDelivery), l'outbox ne le rejoue pas ; la console reste propre.
    if(res?.error?.message==="conversation_blocked"){LANAC_DEBUG&&console.info("[LANAC] message persist : fil bloqué, message refusé par le serveur");return res;}
    //  (B-MOTS, 02/11/2026) une expression BLOQUÉE (22023 « texte_refuse ») — une RÈGLE de la base, comme le fil bloqué : dite sur la ligne, jamais rejouée
    if(texteRefuse(res?.error)!==null){LANAC_DEBUG&&console.info("[LANAC] message persist : texte refusé par la base");return res;}
    if(res?.error) console.error("[LANAC] message persist error:",res.error.message,res.error.code);
    // M4 : livraison au destinataire = SERVEUR-AUTORITATIVE (trigger broadcast_message sur l'INSERT
    // ci-dessus → 'msg' sur lanac:convo + 'message' sur lanac:user). Pont client retiré : plus fiable
    // (part même si le socket de l'émetteur tombe juste après le commit), même latence (post-commit).
    //  (A42) l'échec vient de l'écriture du MESSAGE : la conversation, elle, est écrite — `etape` le dit à la relance.
    return (res?.error&&res.etape!=="conversation")?{...res,etape:"message"}:res;
  }).catch(error=>{
    console.error("[LANAC] message persist exception:",error?.message||error);
    return {error};
  });
  if(finCreation) chaine.then(finCreation,finCreation);
  return chaine;
};
const persistMessagePatch=(convoId,messageId,patch,label="message update")=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId||!isUUID(messageId)) return Promise.resolve({error:null});
  const doUpdate=()=>client.from("messages").update(patch).eq("id",messageId).eq("convo_id",convoId);
  return doUpdate()
    .then(res=>{
      // 42501 = JWT expiré au moment de l'écriture : re-vérifier la session (sans rotation) puis 1 réessai.
      if(res?.error?.code==="42501"){
        return client.auth.getSession().then(({data})=>data?.session?doUpdate():res).catch(()=>res);
      }
      return res;
    })
    .then(res=>{if(res?.error)console.error(`[LANAC] ${label} error:`,res.error.message,res.error.code);return res;})
    .catch(error=>{console.error(`[LANAC] ${label} exception:`,error?.message||error);return {error};});
};
// MSG-DATA-2 — INSERT d'un message SEUL (la conversation existe déjà), via la passerelle. Miroir de
//  persistMessagePatch (garde client/convoId, retry JWT-42501 sans rotation, logging par label) mais pour un
//  INSERT `messageToDB`. À NE PAS confondre avec persistConvoAndMessage (qui upsert AUSSI la conversation) :
//  ici on n'écrit QUE le message (ex. ligne de journal de décision persistée après succès RPC).
// ── A1 (27/07/2026) · RÉSOLUTION DE LA CONVERSATION D'UN DEAL, indépendante de ce qui est CHARGÉ ──
//
//  LE DÉFAUT CORRIGÉ — 11 sites transactionnels retrouvaient « la conversation de cette annonce
//  entre cet acheteur et ce vendeur » par un `convos.filter(...)` sur la liste EN MÉMOIRE, puis y
//  écrivaient. Or `convos` n'est PAS garanti complet : cache local borné, éviction, et demain la
//  pagination de l'inbox (P5-d). Conversation absente ⇒ le filtre ne rend rien ⇒ **l'écriture ne
//  part pas, EN SILENCE**. Un message transactionnel perdu, sans erreur, sans trace.
//
//  ⭐ POURQUOI UNE PASSERELLE QUI **ÉCRIT**, ET NON QUI RETOURNE LA LISTE — les 11 sites ignorent
//  déjà la promesse de `persistConvoAndMessage`. En encapsulant « résoudre PUIS écrire », le
//  call-site reste une ligne d'apparence synchrone : **l'asynchrone ne fuit pas** dans le chemin
//  transactionnel. C'était le vrai risque de ce chantier.
//
//  ⚠️ NE REMPLACE QUE LA MOITIÉ « ÉCRITURE ». Chaque site applique le même prédicat DEUX fois :
//  une fois dans `setConvos(...)` (mise à jour OPTIMISTE locale) et une fois pour persister. La
//  moitié optimiste RESTE locale — on ne peut pas rendre une conversation qu'on n'a pas, et c'est
//  correct ainsi. Seule l'écriture doit être exacte.
//
//  GARDE PRÉSERVÉE À L'IDENTIQUE : plusieurs sites s'écrivaient `c.refId===x && buyerId && …`.
//  Sans buyerId, aucune conversation ne matchait ⇒ aucune écriture. Le `!buyerId` ci-dessous
//  reproduit ce comportement exactement (les variantes sans la garde donnaient le même résultat :
//  `participants.includes(undefined)` est faux).
//
//  SÉCURITÉ : le repli lit `conversations` avec le JETON DE L'UTILISATEUR ⇒ la RLS
//  (`conversations_select_participants`) s'applique — impossible de résoudre une conversation dont
//  on n'est pas participant. Aucune RPC, aucun grant, aucun SECURITY DEFINER : la surface
//  n'est pas élargie. Indexé (`idx_conversations_ref` + GIN sur `participants`) : pas de scan.
//
//  COÛT AUJOURD'HUI : NUL. Tant que l'inbox charge tout, la branche mémoire répond toujours et le
//  repli n'est jamais atteint — zéro requête, zéro egress ajouté (vérifiable par S35).
const dealConvoMatch=(c,refId,buyerId,sellerId)=>
  !!c&&c.refId===refId&&Array.isArray(c.participants)
  &&c.participants.includes(buyerId)&&c.participants.includes(sellerId);
const resolveDealConvos=(convos,{refId,buyerId,sellerId})=>{
  if(!refId||!buyerId||!sellerId) return Promise.resolve([]); // garde `buyerId &&` d'origine
  const local=(convos||[]).filter(c=>dealConvoMatch(c,refId,buyerId,sellerId));
  if(local.length) return Promise.resolve(local);             // chemin d'aujourd'hui : 0 requête
  const client=lanacSupabaseClient();
  if(!client) return Promise.resolve([]);
  return client.from("conversations").select(INBOX_CONVO_SELECT)
    .eq("ref_id",refId).contains("participants",[buyerId,sellerId])
    .then(res=>{
      if(res?.error){console.error("[LANAC] resolveDealConvos error:",res.error.message,res.error.code);return [];}
      return (res?.data||[]).map(convoFromDB).filter(c=>dealConvoMatch(c,refId,buyerId,sellerId));
    },err=>{console.error("[LANAC] resolveDealConvos exception:",err?.message||err);return [];});
};
//  Lot C · C2b (16/10/2026) — LA CARTE DE RÉSERVATION ACCEPTÉE d'un fil dont les messages ne sont pas en mémoire (l'inbox ne
//   charge que ses en-têtes ; les messages viennent à l'ouverture du fil) : UNE lecture bornée — au plus cinq lignes, les seules
//   colonnes du geste —, sous la RLS du membre. La plus récente qui n'est ni tamponnée ni close par la base : celle que la fiche
//   confie à la commande (close_engagement, denounce_engagement), au lieu de tamponner elle-même.
//  (lot C · C2c, 16/10) `memeClose` : la DERNIÈRE carte acceptée, tamponnée ou non — conclure (« J'ai récupéré », « Vendu »)
//   après la conclusion de l'autre partie vise la carte qu'il a tamponnée : la commande retrouve l'accord qui m'attend.
//  (lot C · C2d-4b, 17/10/2026) … la plus récente PAR LA BASE (`seq`, l'ordre qu'elle attribue dans le fil) — jamais par `ts`, la date
//   de l'appareil : une carte d'un autre cycle datée dans le futur passait devant, et la commande, qui juge par `seq`, la refusait.
const acceptedReservationCard=async(convoId,{memeClose=false}={})=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId) return null;
  try{
    const {data,error}=await client.from("messages").select("id,convo_id,kind,status,data,ts,seq").eq("convo_id",convoId).eq("kind","reserve")
      .in("status",["accepted","counterAccepted"]).order("seq",{ascending:false,nullsFirst:false}).limit(5);
    if(error){console.error("[LANAC] acceptedReservationCard error:",error.message,error.code);return null;}
    const m=(data||[]).find(r=>memeClose||!(r.data&&(r.data.reservationAction||r.data.autoClosedReason)));
    return m?{id:m.id,convoId:m.convo_id,kind:m.kind,status:m.status,data:m.data||{}}:null;
  }catch(err){console.error("[LANAC] acceptedReservationCard exception:",err?.message||err);return null;}
};
//  (lot C · C2d-4b) … et EN MÉMOIRE, la même règle : parmi les messages d'UN fil qui répondent au critère, celui au plus grand `seq`
//   (le fil en mémoire est rangé par `ts`) ; sans `seq` (un message optimiste, jamais une carte acceptée), le dernier, comme avant.
const laPlusRecenteParLaBase=(messages,critere)=>{
  let laMeilleure=null,sonSeq=-Infinity,laDerniere=null;
  for(const m of messages||[]){
    if(!m||!critere(m)) continue;
    laDerniere=m;
    const s=m.seq!=null&&Number.isFinite(Number(m.seq))?Number(m.seq):null;
    if(s!=null&&s>sonSeq){laMeilleure=m;sonSeq=s;}
  }
  return laMeilleure||laDerniere;
};
//  ⭐ Lot C · C2d-4b (17/10/2026, relecture de la correction) — CE QUE LA BASE DIT D'UNE RÉPONSE À UN DOSSIER, lu PAREIL par la
//   carte (useCardActions) et par le fil (useTransactionActions).
//   (1) L'ISSUE D'UN VOTE est celle de la commande (`resolution` ; sur un rejeu, l'issue du dossier que CE vote a clos —
//   `par_moi`), jamais celle des votes que l'appareil connaît : le vote de l'autre peut ne pas lui être parvenu — l'écran
//   attendait quand la base avait conclu « effectué / effectué » (ni l'accord, ni l'annonce ne suivaient), ou concluait sur un
//   vote que l'autre avait changé. `null` : la base ne dit rien (une carte d'avant les dossiers) — le calcul de l'appareil reste.
//   (2) LA CLÉ D'UN GESTE sur un dossier — le dossier, le geste, son auteur : le journal de la carte en tire son identifiant
//   (stableUUID), l'avis à l'autre partie sa clé de déduplication (`dedup_key`, par destinataire, en base). Un rejeu — la réponse
//   perdue, un second appareil, un écran en retard — ne les écrit jamais deux fois (le message existe déjà : 23505, un succès ;
//   l'avis : ignoré). `null` sans dossier : l'identifiant d'avant (aléatoire).
const LANAC_STATUT_DU_VOTE={pending:"final_pending",reconciled:"resolved_final_done",settled_no_fault:"resolved_final_not_done",benefit_of_doubt:"disputed"};
const LANAC_STATUT_DU_DOSSIER={done:"resolved_final_done",not_done:"resolved_final_not_done",unresolved_disputed:"disputed"};
const statutDuVoteParLaBase=reponse=>{
  if(!reponse||typeof reponse!=="object") return null;
  if(reponse.noop===true) return reponse.par_moi===true&&typeof reponse.final_result==="string"?(LANAC_STATUT_DU_DOSSIER[reponse.final_result]||null):null;
  return typeof reponse.resolution==="string"?(LANAC_STATUT_DU_VOTE[reponse.resolution]||null):null;
};
const cleDuGesteSurLeDossier=(reportId,action,auteurId)=>reportId&&action&&auteurId?`reponse-dossier:${reportId}:${action}:${auteurId}`:null;
// Écrit un message dans TOUTES les conversations du deal (résolution ci-dessus). Fire-and-forget,
//  comme les `.forEach(persistConvoAndMessage)` qu'elle remplace : la promesse peut être ignorée.
const postToDealConvos=(convos,cible,message)=>
  resolveDealConvos(convos,cible).then(list=>Promise.all(list.map(c=>persistConvoAndMessage(c,message))));
//  (lot C · C5-4d, 20/10/2026) patchDealConvos RETIRÉE : son dernier appelant (la carte d'incident depuis la fiche) est parti avec C5-3c-1 —
//   la base écrit ces cartes ; un UPDATE de l'appareil sur elles serait rétabli en silence.
//  (lot C · C5-4d, 20/10/2026) closeDealConfirmCards RETIRÉE : plus aucun appelant (la confirmation d'un accord ferme sa carte dans sa
//   commande depuis C3-3) ; c'était un UPDATE de l'appareil sur les cartes « Transaction à confirmer ».

// ── C7 · T14 (16/09/2026) · UNE ANNONCE SE RÉSOUT, MÊME HORS DE L'ÉCHANTILLON ────────────────────────────────────────────
//  Jumeau de resolveDealConvos, pour les ANNONCES. `items` n'est qu'un échantillon : les 200 plus récentes, mes annonces,
//  favoris et vues récentes. L'annonce d'un fil peut donc manquer en mémoire alors qu'elle existe — et une quinzaine de
//  gestes (« Essai effectué », « Visite effectuée », « Réserver au prix convenu », report du retrait, décisions de
//  l'acheteur, invitation après essai, lien partagé…) s'arrêtaient alors SANS UN MOT, ou partaient sur le mauvais chemin
//  (fuseau de Luxembourg au lieu de celui de l'annonce, vote immobilier pris pour un geste simple).
//
//  ⭐ LA RÈGLE : mémoire d'abord — AUCUNE requête quand l'annonce est chargée (le chemin d'aujourd'hui, octet pour
//  octet) ; sinon UNE ligne lue par son id, partagée entre les demandes simultanées, gardée un temps, absence comprise.
//  ⭐ À PART DE L'ÉCHANTILLON : une annonce résolue ne rejoint jamais `items` — ni les rails de l'accueil, ni le cache
//  de démarrage, ni la pagination du marché ne changent. Les flux la lisent par `listingOf` (19-app).
//  PROJECTION : la ligne ENTIÈRE, comme les trois replis par id qui existaient déjà (fiche depuis un message, depuis une
//  notification, depuis la carte) — elle sert le geste, l'écran du fil ET la fiche, sans seconde lecture d'hydratation.
//  SÉCURITÉ : lecture avec le jeton de l'utilisateur ⇒ la RLS (`listings_select_public`) s'applique ; une annonce en
//  modération reste invisible à l'autre partie (« absente »). Aucune RPC, aucun droit nouveau.
//  ⚠️ PAR-UTILISATEUR (la RLS dépend du compte) ⇒ purgé dans lanacResetPerUserState ; une réponse d'un compte précédent
//  arrivée après le changement est ignorée (génération).
const LISTING_RESOLVE_TTL_MS=5*60000;     // affichage : une annonce lue sert 5 min
const LISTING_RESOLVE_GESTURE_MS=60000;   // geste : ce qui a plus d'une minute est relu
const LISTING_RESOLVE_MISS_TTL_MS=60000;  // absence confirmée par la base : 1 min
const LISTING_RESOLVE_ERROR_TTL_MS=30000; // panne : un appel « d'arrière-plan » ne relance pas avant 30 s
const LISTING_BACKGROUND_ERROR_MS=5*60000; // panne : la réconciliation ne relance pas avant 5 min
const LISTING_RESOLVE_MAX=100;            // borne dure (les plus anciennes sortent d'abord)
const lanacListingStore={gen:0,version:0,snapshot:[],entries:new Map(),inflight:new Map(),subs:new Set()};
const listingKey=id=>{const k=id==null?"":String(id).trim();return /^\d{1,19}$/.test(k)?k:null;};
const listingStorePublish=()=>{
  const s=lanacListingStore,next=[];
  s.entries.forEach(e=>{if(e.item)next.push(e.item);});
  s.snapshot=next;s.version+=1;
  s.subs.forEach(fn=>{try{fn();}catch(e){}});
};
const listingStorePut=(key,entry)=>{
  const s=lanacListingStore,prev=s.entries.get(key);
  s.entries.delete(key);s.entries.set(key,entry);
  let changed=(prev&&prev.item)!==entry.item;
  while(s.entries.size>LISTING_RESOLVE_MAX){const k=s.entries.keys().next().value;if(s.entries.get(k)?.item)changed=true;s.entries.delete(k);}
  if(changed) listingStorePublish();
};
const peekResolvedListing=id=>{const k=listingKey(id);return (k&&lanacListingStore.entries.get(k)?.item)||null;};
// "known" (une ligne est en main, même ancienne) · "absent" (la base a dit non ; un geste ou l'écran relira après 1 min,
//  l'arrière-plan, lui, s'en tient là) · "unknown" (jamais lue, ou panne).
const resolvedListingState=id=>{
  const k=listingKey(id),e=k&&lanacListingStore.entries.get(k);
  if(!e) return "unknown";
  if(e.item) return "known";
  if(e.miss) return "absent";
  return "unknown";
};
// → Promise<{item, source}> ; source ∈ memory | cache | server | absent | stale | error | offline. Ne rejette jamais.
//  opts.local : l'annonce déjà trouvée en mémoire (0 requête) · opts.maxAgeMs : fraîcheur exigée (défaut 5 min)
//  opts.background : respecte le recul après une panne (la réconciliation ne martèle pas un serveur qui tousse).
const resolveListing=(id,opts={})=>{
  if(opts.local) return Promise.resolve({item:opts.local,source:"memory"});
  const key=listingKey(id);
  if(!key) return Promise.resolve({item:null,source:"absent"});
  const s=lanacListingStore,now=Date.now(),e=s.entries.get(key);
  const maxAge=Number.isFinite(opts.maxAgeMs)?opts.maxAgeMs:LISTING_RESOLVE_TTL_MS;
  if(e){
    if(e.item&&now-e.at<=maxAge) return Promise.resolve({item:e.item,source:"cache"});
    if(e.miss&&now-e.at<=Math.min(maxAge,LISTING_RESOLVE_MISS_TTL_MS)) return Promise.resolve({item:null,source:"absent"});
    if(opts.background&&e.errorAt&&now-e.errorAt<=LISTING_RESOLVE_ERROR_TTL_MS) return Promise.resolve({item:e.item||null,source:e.item?"stale":"error"});
  }
  const enVol=s.inflight.get(key);
  if(enVol) return enVol;
  const client=lanacSupabaseClient();
  if(!client) return Promise.resolve({item:e?.item||null,source:e?.item?"stale":"offline"});
  const gen=s.gen;
  const echec=err=>{
    if(gen!==s.gen) return {item:null,source:"error",error:err};
    const cur=s.entries.get(key);
    listingStorePut(key,{...(cur||{item:null,at:0,miss:false}),errorAt:Date.now()});
    // une ligne déjà en main reste la meilleure connaissance (comme `items`, que rien ne rafraîchit non plus) ;
    //  sans elle, la panne est DITE à l'appelant, jamais prise pour une absence.
    return cur&&cur.item?{item:cur.item,source:"stale",error:err}:{item:null,source:"error",error:err};
  };
  const p=client.from("listings").select("*").eq("id",key).maybeSingle().then(res=>{
    if(res?.error){console.error("[LANAC] resolveListing error:",res.error.message,res.error.code);return echec(res.error);}
    if(gen!==s.gen) return {item:null,source:"error",error:{message:"account_changed"}};
    const item=res?.data?listingFromDB(res.data):null;
    listingStorePut(key,item?{item,at:Date.now(),miss:false}:{item:null,at:Date.now(),miss:true});
    return {item,source:item?"server":"absent"};
  },err=>{console.error("[LANAC] resolveListing exception:",err?.message||err);return echec(err);});
  s.inflight.set(key,p);
  p.then(()=>{if(s.inflight.get(key)===p)s.inflight.delete(key);});
  return p;
};
// ── (05/10/2026, A20 lot 2) LES PHOTOS DES ANNONCES MAIGRES, PAR LOTS — pour le plein écran du fil vivant ─────────────────
//  L'échantillon de l'accueil ne porte que la couverture (`img`) : `photos` en a été retiré le 31/07 (EGRESS P3c, 12,6 Ko sur
//  200 lignes). Le plein écran fait défiler les photos d'une annonce SANS ouvrir sa fiche : il lui faut `photos`, et rien
//  d'autre. MESURÉ sur la base de production (05/10) : 66 o par annonce en moyenne — un lot de 8 ≈ 1 Ko décodé, là où la
//  fiche lit la ligne ENTIÈRE (≈ 1,7 Ko) pour UNE annonce.
//   · UNE requête par lot, PHOTOS_LOT_MAX annonces au plus : `select id,photos … id=in.(…)` ;
//   · une annonce déjà lue, ou en vol, n'est JAMAIS redemandée de la session ; une annonce que la base ne rend pas (retirée,
//     invisible sous la RLS) est notée « sans autre photo » — pas redemandée non plus ;
//   · une panne ne note rien : le prochain arrêt sur l'annonce pourra réessayer (un arrêt, un essai — jamais une boucle) ;
//   · borné (PHOTOS_GARDEES annonces, les plus anciennes sortent d'abord) ;
//   · ⚠️ PAR-UTILISATEUR (la RLS dépend du compte) ⇒ purgé dans lanacResetPerUserState ; une réponse du compte précédent
//     arrivée après le changement est ignorée (génération).
const PHOTOS_LOT_MAX=8, PHOTOS_GARDEES=400;
const lanacPhotosStore={gen:0,vues:new Map(),enVol:new Map()};
const photosDeLAnnonce=id=>{ const e=lanacPhotosStore.vues.get(listingKey(id)||""); return e?e.photos:null; };
const lirePhotosDesAnnonces=ids=>{
  const s=lanacPhotosStore;
  const cles=[...new Set((ids||[]).map(listingKey).filter(Boolean))].filter(k=>!s.vues.has(k)&&!s.enVol.has(k)).slice(0,PHOTOS_LOT_MAX);
  const client=lanacSupabaseClient();
  if(!cles.length||!client) return Promise.resolve(0);
  const gen=s.gen;
  const fin=()=>cles.forEach(k=>{ if(s.enVol.get(k)===p) s.enVol.delete(k); });
  const p=client.from("listings").select("id,photos").in("id",cles).then(res=>{
    fin();
    if(gen!==s.gen||!res||res.error) return 0;
    const lues=new Map((res.data||[]).map(r=>[String(r.id),Array.isArray(r.photos)?r.photos.filter(x=>typeof x==="string"&&x):[]]));
    const t=Date.now();
    cles.forEach(k=>s.vues.set(k,{photos:lues.get(k)||[],at:t}));
    while(s.vues.size>PHOTOS_GARDEES){ const plusVieille=s.vues.keys().next().value; s.vues.delete(plusVieille); }
    return cles.length;
  },()=>{ fin(); return 0; });
  cles.forEach(k=>s.enVol.set(k,p));
  return p;
};
// Plusieurs annonces JAMAIS lues, en une requête par tranche de 50 (réconciliation) : ce qui est déjà en main, déjà dit
//  absent, en vol, ou en recul après une panne n'est pas redemandé. Ne rafraîchit rien : l'arrière-plan n'achète ni
//  fraîcheur ni nouvelle chance — une annonce absente le reste pour lui jusqu'à ce qu'un geste ou l'écran la relise.
const resolveListingsInBackground=ids=>{
  const s=lanacListingStore,now=Date.now();
  const keys=[...new Set((ids||[]).map(listingKey).filter(Boolean))].filter(k=>{
    const e=s.entries.get(k);
    if(s.inflight.has(k)) return false;
    if(e&&(e.item||e.miss||(e.errorAt&&now-e.errorAt<=LISTING_BACKGROUND_ERROR_MS))) return false;
    return true;
  });
  const client=lanacSupabaseClient();
  if(!keys.length||!client) return Promise.resolve(0);
  const gen=s.gen,lots=[];
  for(let i=0;i<keys.length;i+=50) lots.push(keys.slice(i,i+50));
  return Promise.all(lots.map(lot=>{
    const p=client.from("listings").select("*").in("id",lot).then(res=>{
      if(gen!==s.gen) return 0;
      if(res?.error){
        console.error("[LANAC] resolveListingsInBackground error:",res.error.message,res.error.code);
        lot.forEach(k=>{const cur=s.entries.get(k);listingStorePut(k,{...(cur||{item:null,at:0,miss:false}),errorAt:Date.now()});});
        return 0;
      }
      const vus=new Map();
      (res?.data||[]).forEach(row=>{const it=listingFromDB(row);if(it)vus.set(String(it.id),it);});
      const t=Date.now();
      lot.forEach(k=>listingStorePut(k,vus.has(k)?{item:vus.get(k),at:t,miss:false}:{item:null,at:t,miss:true}));
      return vus.size;
    },err=>{console.error("[LANAC] resolveListingsInBackground exception:",err?.message||err);return 0;});
    // un geste qui arrive pendant le vol attend CETTE lecture et reçoit SA ligne (pas le compte du lot)
    const enVol=new Map(lot.map(k=>[k,p.then(()=>{
      const e=s.entries.get(k);
      return e&&e.item?{item:e.item,source:"server"}:e&&e.miss?{item:null,source:"absent"}:{item:null,source:"error"};
    })]));
    enVol.forEach((q,k)=>s.inflight.set(k,q));
    p.then(()=>enVol.forEach((q,k)=>{if(s.inflight.get(k)===q)s.inflight.delete(k);}));
    return p;
  })).then(ns=>ns.reduce((a,b)=>a+b,0));
};
// Des lignes lues ailleurs (file de modération de l'admin) rejoignent la réserve — mêmes règles, même borne.
const rememberListings=rows=>{
  const t=Date.now();
  (rows||[]).forEach(row=>{const it=row&&("catId" in row?row:listingFromDB(row));const k=it&&listingKey(it.id);if(k)listingStorePut(k,{item:it,at:t,miss:false});});
};
// Le geste optimiste suit l'annonce où qu'elle vive : le MÊME `p=>p.map(...)` que reçoit `items` s'applique à la réserve.
//  Une annonce retirée par le geste (filtre) y devient absente ; aucune annonce n'y ENTRE par ce chemin.
const patchResolvedListings=updater=>{
  const s=lanacListingStore;
  if(!s.snapshot.length||typeof updater!=="function") return;
  let next;
  try{next=updater(s.snapshot);}catch(e){return;}
  if(!Array.isArray(next)||next===s.snapshot) return;
  const parCle=new Map();
  next.forEach(it=>{const k=it&&listingKey(it.id);if(k)parCle.set(k,it);});
  let changed=false;
  s.entries.forEach((e,k)=>{
    if(!e.item) return;
    const n=parCle.get(k);
    if(!n){s.entries.set(k,{...e,item:null,miss:true});changed=true;}
    else if(n!==e.item){s.entries.set(k,{...e,item:n});changed=true;}
  });
  if(changed) listingStorePublish();
};
const subscribeResolvedListings=fn=>{lanacListingStore.subs.add(fn);return ()=>{lanacListingStore.subs.delete(fn);};};
const resolvedListingsSnapshot=()=>lanacListingStore.snapshot;

const insertMessage=(convoId,message,label="message insert")=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId||!message) return Promise.resolve({error:null});
  const row=messageToDB(message,convoId);
  const doInsert=()=>client.from("messages").insert(row);
  return doInsert()
    .then(res=>{
      if(res?.error?.code==="42501"){
        return client.auth.getSession().then(({data})=>data?.session?doInsert():res).catch(()=>res);
      }
      return res;
    })
    // 23505 sur un INSERT = la ligne visée EXISTE DÉJÀ ⇒ succès (écritures idempotentes par
    //  conception : id déterministes du sweep N2, index anti-doublon d'incident A2).
    //  ⚠️ Volontairement PAS appliqué à persistMessagePatch (UPDATE) : là, un 23505 veut dire
    //  qu'une AUTRE ligne occupe la clé — ce n'est pas « l'intention est satisfaite », et ça
    //  doit rester une erreur visible.
    .then(res=>{
      if(res?.error?.code==="23505"){
        LANAC_DEBUG&&console.info(`[LANAC] ${label} : ligne déjà présente (23505) — no-op idempotent`);
        return {error:null,data:res.data??null};
      }
      if(res?.error)console.error(`[LANAC] ${label} error:`,res.error.message,res.error.code);
      return res;
    })
    .catch(error=>{console.error(`[LANAC] ${label} exception:`,error?.message||error);return {error};});
};
// ── Passerelle RPC (MSG-RPC-1, 22/07) : centralise les APPELS RPC de la messagerie hors des composants/hooks.
//  Un seul point par RPC = nom + forme des params maîtrisés (surface d'appel unique, comme les writes). ──
// set_conversation_flags : archive/suppression d'une conversation (drapeaux par-utilisateur, serveur-autoritaire).
//  `archived`/`deleted` ∈ {true, false, undefined} ; undefined ⇒ p_* = null = « ne pas toucher ce drapeau ».
//  Renvoie le THENABLE Supabase → les appelants gardent leur handling EXACT (fire-and-forget loggé / await+
//  réconciliation) ; appel SINGLE-SHOT (F3, via _rpcCall) — les REJETS réseau se PROPAGENT (iso).
/** @param {string} convoId @param {{archived?:boolean, deleted?:boolean}} [flags] */
const rpcSetConversationFlags=(convoId,flags={})=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId) return Promise.resolve({error:null}); // shape no-client conservée à l'identique
  return _rpcCall("set_conversation_flags",{p_convo:convoId,p_archived:flags.archived===undefined?null:!!flags.archived,p_deleted:flags.deleted===undefined?null:!!flags.deleted}); // REVIEW-FIX F4 : délégué à _rpcCall (single-shot) (source unique)
};
// get_presence : LECTURE de la présence (dernier vu) d'une liste d'utilisateurs. Centralise le nom RPC + la forme
//  du param (p_user_ids: string[]). Renvoie le THENABLE {data,error} → les appelants gardent leur `.then(({data})=>…)`
//  EXACT (traitement de `data`, rejet avalé). Guard liste vide/absent client ⇒ {data:null} (aucune présence).
/** @param {string[]} userIds */
const rpcGetPresence=(userIds)=>{
  const client=lanacSupabaseClient();
  if(!client||!Array.isArray(userIds)||!userIds.length) return Promise.resolve({data:null});
  return _rpcCall("get_presence",{p_user_ids:userIds}); // REVIEW-FIX F4 : délégué à _rpcCall (single-shot)
};
// mark_convo_read : marque la conversation lue (watermark serveur) et RENVOIE {my_read_seq, states[]} → l'appelant
//  en tire son read_seq réel + le « Vu » (✓✓) de l'autre. Centralise le nom RPC + le param (p_convo) ; single-shot (F3).
//  Renvoie le THENABLE {data,error} → le handler riche de l'appelant reste EXACT. Guard client/convoId ⇒ {data:null}.
/** @param {string} convoId */
const rpcMarkConvoRead=(convoId)=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId) return Promise.resolve({data:null});
  return _rpcCall("mark_convo_read",{p_convo:convoId}); // REVIEW-FIX F4 : délégué à _rpcCall (single-shot)
};
// decide_request : DÉCISION de deal serveur-autoritaire (accept/decline/counter d'offre/réservation/visite). RPC
//  ATOMIQUE (garde serveur : re-décider est refusé). Centralise le nom + params (p_message_id/p_action/p_options)
//  (single-shot, F3). ⚠️ SÛR sans retry : un 42501 visible client ⇒ fonction 1-txn ROULÉE EN ARRIÈRE (rien committé) ⇒
//  jamais de double-décision, avec ou sans ré-essai. (NB rationale : ici 42501
//  est un rejet MÉTIER, pas « jeton expiré » — cf. le bloc corrigé au-dessus de _rpcCall.) Renvoie le THENABLE {data,error} →
//  l'appelant garde son `.then(({error})=>{ err⇒revert ; ok⇒journal+commit }).catch(revert)` EXACT (fire unique).
/** @param {string} messageId @param {string} action @param {object} [options] */
const rpcDecideRequest=(messageId,action,options)=>{
  const client=lanacSupabaseClient();
  if(!client||!messageId) return Promise.resolve({error:null});
  return _rpcCall("decide_request",{p_message_id:messageId,p_action:action,p_options:options||{}}); // REVIEW-FIX F4 : délégué à _rpcCall (single-shot)
};
//  B7 (14/10/2026) — REFORMULER SA DEMANDE DE REPORT : la commande (refresh_my_request). L'acheteur redit le retrait et la
//   durée de SA demande de report ; la base la remet « en attente », efface les propositions du vendeur devenues sans objet,
//   calcule l'échéance et tait la note sous blocage. Refus NOMMÉS (request_closed, reservation_not_live, slot_invalid,
//   slot_out_of_range…).
/** @param {string} messageId @param {string} pickupAt @param {number} durationHours @param {string|null} note */
const rpcRefreshMyRequest=(messageId,pickupAt,durationHours,note)=>{
  const client=lanacSupabaseClient();
  if(!client||!messageId) return Promise.resolve({error:{message:"no_supabase_client",code:"NO_CLIENT"}});
  return _rpcCall("refresh_my_request",{p_message_id:messageId,p_pickup_at:pickupAt,p_duration_hours:durationHours,p_note:note||null});
};
// D3 ter (26/09/2026) : RETIRER SA DEMANDE — la commande entière (withdraw_my_request) : la base retrouve MA demande (sur
//  cette annonce, ou celle que le fil nomme), la retire, écrit le journal et l'avis à l'autre partie dans la même transaction.
//  IDEMPOTENTE : `journalId` vient de l'appareil (le journal optimiste EST la ligne de la base) — un double appui ou un geste
//  rejoué rend l'état, n'écrit rien deux fois. Single-shot (F3) : ses refus sont MÉTIER et NOMMÉS (request_accepted,
//  request_closed, request_not_found, not_your_request) — l'appelant les dit. Renvoie le THENABLE {data,error}.
/** @param {number|string} listingId @param {string|null} messageId @param {string} journalId */
const rpcWithdrawMyRequest=(listingId,messageId,journalId)=>_rpcCall("withdraw_my_request",{p_listing_id:Number(listingId),p_message_id:messageId||null,p_journal_id:journalId||null});
// 27/09/2026 : ANNULER SA RÉSERVATION ACCEPTÉE — la commande entière (cancel_my_reservation) : la base retrouve MA réservation
//  (sur cette annonce, ou la carte que le fil nomme), libère l'annonce (la brique de l'annonce ; son carton sur la carte annulée, C2d-4),
//  estampille UNE carte, écrit le journal, réarme l'offre d'origine et prévient le vendeur — UNE transaction. IDEMPOTENTE
//  (`journalId` vient de l'appareil). Single-shot (F3) : refus MÉTIER et NOMMÉS (reservation_closed, reservation_already_picked_up,
//  listing_finalized, not_your_reservation, request_pending, reservation_not_found) — l'appelant les dit. THENABLE {data,error}.
/** @param {number|string} listingId @param {string|null} messageId @param {string} journalId */
const rpcCancelMyReservation=(listingId,messageId,journalId)=>_rpcCall("cancel_my_reservation",{p_listing_id:Number(listingId),p_message_id:messageId||null,p_journal_id:journalId||null});
// cast_incident_resolution : VOTE de résolution d'incident (lié à auth.uid serveur, non-forgeable) — accumule les
//  votes et ANNULE le carton/clôt le report si les 2 parties concordent. Centralise le nom + params (p_report_id/
//  p_vote) ; single-shot (F3). SÛR : 42501=RLS dénie=vote non enregistré (rollback). Fire-and-forget
//  côté appelants ; renvoie le THENABLE {data,error} → leur `.then(({error})=>…).catch(()=>{})` reste EXACT.
/** @param {string} reportId @param {string} vote */
const rpcCastIncidentResolution=(reportId,vote)=>{
  const client=lanacSupabaseClient();
  if(!client||!reportId) return Promise.resolve({error:null});
  return _rpcCall("cast_incident_resolution",{p_report_id:reportId,p_vote:vote}); // REVIEW-FIX F4 : délégué à _rpcCall (single-shot)
};
// Compteurs d'inbox : 3 RPC de LECTURE sans paramètre (agrégats serveur RLS-scopés auth.uid, best-effort avec repli
//  local par scan côté client). Cœur commun _rpcCall (nom en variable ⇒ hors du régime littéral du garde) : appel +
//  single-shot (F3) : sur 42501/erreur, l'appelant retombe sur son scan local (fallback conçu). Renvoie le THENABLE →
//  les appelants gardent leur `await` / `.then(r,err)` EXACT. 3 façades NOMMÉES (sémantique + surface unique).
//  TX-RPC-1 : `params` optionnel généralise le cœur aux RPC PARAMÉTRÉS sans toucher les 3 compteurs
//  (garde `params===undefined` ⇒ appel NO-param strictement identique à RPC-6).
//  ⚠️⚠️ RATIONALE 42501 — CORRIGÉ le 22/07 après audit du SQL réel (pg_proc.prosrc des 20 RPC gérés) :
//   • CE QUI EST VRAI (et prouvé) : un `42501` VISIBLE CLIENT ⇒ la fonction (toutes `prokind='f'`, 1 txn, aucun `COMMIT`)
//     a ROULÉ EN ARRIÈRE l'intégralité de son effet ⇒ même un ré-essai (là où il existe : écritures brutes RLS) ne peut
//     JAMAIS doubler une écriture ; côté RPC on est de toute façon single-shot. Garantie de fond, elle tient pour tous.
//   • CE QUI ÉTAIT FAUX (mon ancien commentaire « 42501 = JWT expiré ») : pour ces RPC `SECURITY DEFINER`, `42501` est
//     le plus souvent un RAISE **MÉTIER** (not_owner/not_a_party/anchor_not_accepted…) ; le vrai cas jeton-nul est `28000`
//     (ou `42501` pour grant_listing_boost, ou rien pour set_conversation_flags/mark_convo_read — codes HÉTÉROGÈNES).
//   ⇒ DÉCISION (REVIEW-FIX F3, 22/07) : PAS de retry côté RPC (`_rpcCall` est SINGLE-SHOT) — rejouer un rejet métier ne
//     recouvrait rien et gaspillait 1 aller-retour par déni. Le retry JWT-42501 reste UNIQUEMENT sur les écritures BRUTES
//     RLS (persist* messages/conversations : là 42501 peut = uid nul, cas fréquent et recouvrable). Restaure le single-shot
//     d'AVANT la passerelle ⇒ zéro régression, et retire du même coup toute la surface F1 (retry-leg) côté RPC.
const _rpcCall=(name,params)=>{
  const client=lanacSupabaseClient();
  if(!client) return Promise.resolve({data:null});
  // single-shot (F3) — cf. bloc RATIONALE. `Promise.resolve(...)` garantit une VRAIE Promise (le PostgrestBuilder nu n'a
  //  pas `.catch/.finally`) — iso avec le retour du chantier (qui renvoyait `call().then(...)`) ; les rejets réseau propagent.
  return Promise.resolve(params===undefined?client.rpc(name):client.rpc(name,params));
};
const rpcMyFailedOfferCounts=()=>_rpcCall("my_failed_offer_counts");
const rpcInboxCounters=()=>_rpcCall("inbox_counters");
// A36 · 2d (12/10) : la REPRISE — un curseur (ms, horloge du serveur), le fil ouvert (ou null) ; un appel, borné, idempotent.
const rpcReprise=(depuisMs,fil)=>_rpcCall("lanac_reprise",{p_depuis_ms:Math.max(0,Math.floor(Number(depuisMs)||0)),p_fil:fil||null});
// A38 (12/10) : LE PROFIL D'INTÉRÊT du membre connecté, tel que la base le calcule (fil_profil : clés, poids relatif, poids brut) — lu À LA
//  DEMANDE, pour « Pourquoi cet ordre ? » ; jamais au démarrage (le fil, lui, est rangé par le serveur dans fil_echantillon).
const rpcFilProfil=()=>_rpcCall("fil_profil");
// C7 · T15 : ce qui engage le compte appelant (accords, annonces, réservations, demandes), en nombres — la mesure que la
//  suppression de compte lit elle-même (delete_user_data). Lue avant toute purge de médias.
const rpcMyAccountEngagements=()=>_rpcCall("my_account_engagements");
// P5-d : UNE page d'inbox (keyset serveur sur (coalesce(last_message_at,created_at) DESC, id DESC),
//  filtre archive/masque COTE SERVEUR, auth.uid() seul, limite bornee 1..100 par la fonction SQL).
// M4 : recherche SERVEUR sur TOUTES mes conversations (titre d'annonce OU nom de
//  l'interlocuteur), sans accent ni casse des DEUX cotes (immutable_unaccent). auth.uid()
//  seul ; ne renvoie que des ids de MES conversations.
// D2 : RETRAIT d'un message — GOUVERNE PAR LE SERVEUR. Les gardes (auteur seul, texte seul,
//  jamais une carte, message signale intouchable, fenetre 15 min sur l'horloge SERVEUR) vivent
//  dans la fonction SQL, pas ici : un client modifie ne peut pas les contourner. Le texte est
//  CONSERVE en base (la moderation doit pouvoir lire ce qui a ete ecrit) — seul l'affichage change.
const rpcRemoveMessage=(messageId)=>_rpcCall("remove_message",{p_message_id:messageId});
const rpcInboxSearch=(q,limit)=>_rpcCall("inbox_search",{p_q:q,p_limit:limit||30});
const rpcInboxPage=(limit,beforeActivity,beforeId,archived)=>_rpcCall("inbox_page",{p_limit:limit,p_before_activity:beforeActivity||null,p_before_id:beforeId||null,p_archived:!!archived});
const rpcMyUnreadCounts=()=>_rpcCall("my_unread_counts");
//  (lot C · C5-2a, 20/10/2026) rpcApplyReservationRequest RETIRÉE : l'application n'appelle plus apply_reservation_request — la base écrit seule
//   l'annonce d'une réservation (la carte la marque à sa naissance, l'acceptation et le report accepté la réservent, une demande close la rend) ;
//   la commande ne fait plus rien (`reserve_request` refusée par son nom, le reste `noop`) et reste servie aux applications d'avant.
//  (lot C · C2d-3b, 16/10/2026) rpcApplyReservationListingState RETIRÉE : l'application n'appelle plus apply_reservation_listing_state
//   (la libération à l'issue d'un incident appartient à sa commande, C2d-3a) ; la brique sera retirée aux membres (C2d-3c).
// cast_deal_vote (TX-RPC-2) : VOTE de transaction/visite du VOTE-STORE (immo) lié à auth.uid serveur (non-forgeable,
//  jsonb_set par clé d'acteur ⇒ IDEMPOTENT). 2 sites (intent "transaction"/"visit_done"). 42501 = rejet MÉTIER (jeton-nul=28000 ;
//  le handling appelant traite `error||!data` uniformément) ⇒ SÛR par ROLLBACK (single-shot F3 ; 42501=vote non
//  enregistré, rien committé). Renvoie le THENABLE {data,error} → le `.then(({data,error})=>{pending/transformed/noop})
//  .catch(...)` RICHE de chaque appelant reste EXACT. p_marker_message_id passé VERBATIM (null en transaction).
/** @param {string} anchorMessageId @param {string} intentKind @param {string|null} markerMessageId @param {string} vote */
const rpcCastDealVote=(anchorMessageId,intentKind,markerMessageId,vote)=>_rpcCall("cast_deal_vote",{p_anchor_message_id:anchorMessageId,p_intent_kind:intentKind,p_vote:vote,p_marker_message_id:markerMessageId});
// confirm_deal / create_confirmed_deal (TX-RPC-3) : finalisation de deal 2-parties. confirm_deal(p_deal_id) marque le deal
//  confirmé (trigger serveur finalize_listing_on_seller_confirm) ; create_confirmed_deal crée/promeut un deal confirmé
//  (IDEMPOTENT par p_deal_id fourni + operation_key UNIQUE). 42501 = rejet MÉTIER (jeton-nul=28000 ; handling appelant
//  générique) ⇒ SÛR par ROLLBACK (single-shot F3) : 42501=rien committé (et re-confirmer/re-créer le MÊME deal_id serait
//  idempotent de toute façon). Fire-and-forget côté appelants ; THENABLE {error} préservé → leur `.then(({error})=>…).catch(…)` EXACT.
/** @param {string} dealId */
const rpcConfirmDeal=(dealId)=>_rpcCall("confirm_deal",{p_deal_id:dealId});
/** @param {string} itemId @param {string} buyerId @param {string} kind @param {boolean} visitOnly @param {string} dealId */
const rpcCreateConfirmedDeal=(itemId,buyerId,kind,visitOnly,dealId)=>_rpcCall("create_confirmed_deal",{p_item_id:itemId,p_buyer_id:buyerId,p_kind:kind,p_visit_only:visitOnly,p_deal_id:dealId});
// close_incident_report (TX-RPC-4) : clôture serveur d'un signalement d'incident (outcome disputed/acknowledged). Keyé
//  report_id ⇒ IDEMPOTENT (re-clôturer un report déjà clos = no-op). 42501 = rejet MÉTIER (jeton-nul=28000) ⇒ SÛR par
//  ROLLBACK (single-shot F3 ; 42501=non clos, rien committé). Fire-and-forget ; THENABLE {error} → `.then(({error})=>…).catch(…)` EXACT.
/** @param {string} reportId @param {string} outcome */
const rpcCloseIncidentReport=(reportId,outcome)=>_rpcCall("close_incident_report",{p_report_id:reportId,p_outcome:outcome});
// record_trust_strike (TX-RPC-5) : ANTI-FRAUDE — enregistre un carton de confiance (trust_score serveur, non-forgeable).
//  SINGLE-SHOT via _rpcCall (F3) : le passage était « spécialisé » UNIQUEMENT pour exclure `anchor_not_accepted` du retry ;
//  sans retry, cette spécialisation disparaît. NB 42501 ici = rejet MÉTIER (not_a_party/anchor_not_accepted/… ; jeton-nul =
//  28000). ⭐ Aucun double-carton même sans retry : 42501 ⇒ rollback ⇒ rien committé (+ `on conflict do nothing` serveur en
//  2e filet). Renvoie le THENABLE {error} → l'appelant garde son handling EXACT (dont la suppression `!(42501 &&
//  anchor_not_accepted)` du site aveu/dispute, toujours utile pour NE PAS logguer ce rejet légitime).
//  ⭐ Audit 23/09/2026 (lot serveur 1, phase 1) — LE CARTON QU'ON S'IMPUTE À SOI-MÊME N'A PAS DE PARAMÈTRE DE DOSSIER.
//   Les trois appels du client (annulation auto-déclarée du vendeur ×2, rendez-vous annulé) passaient `null` à
//   record_trust_strike — mais le paramètre existait, et un appel direct pouvait y glisser le dossier ouvert contre l'autre
//   partie (un « aveu » à sa place). record_self_strike(fil, sorte) ne l'a pas : un paramètre qui n'existe pas ne se
//   détourne pas. record_trust_strike reste en base pour les applications pas encore à jour (phase 2 : le retirer).
/** @param {string} convoId @param {"seller_cancel"|"rdv_cancel"} kind */
const rpcRecordSelfStrike=(convoId,kind)=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId) return Promise.resolve({error:null}); // shape no-client conservée à l'identique
  return _rpcCall("record_self_strike",{p_convo_id:convoId,p_kind:kind}); // single-shot, comme l'était record_trust_strike (F3)
};
//  ⚠️ L'ancienne passerelle reste EXPORTÉE pour les seuls bancs qui rechargent une version ANTÉRIEURE des hooks (preuves
//   « avant/après », ex. annonce-hors-echantillon, T14) : l'application ne l'appelle plus (incident-rail-honest le vérifie).
//   B7 (14/10/2026) : la commande est RETIRÉE aux appareils (seul report_incident, le serveur, porte un carton contre l'autre
//   partie) — un appel d'ici reçoit désormais un refus de droit ; la passerelle ne sert plus qu'à ces bancs.
/** @param {string} convoId @param {string} kind @param {string|null} sourceReportId */
const rpcRecordTrustStrike=(convoId,kind,sourceReportId)=>{
  const client=lanacSupabaseClient();
  if(!client||!convoId) return Promise.resolve({error:null});
  return _rpcCall("record_trust_strike",{p_convo_id:convoId,p_kind:kind,p_source_report_id:sourceReportId});
};
// Trust-config ADMIN (ADM-RPC-1 : get_trust_config / admin_set_trust_config / admin_activate_trust_config_version) — PLAN
//  DE CONTRÔLE du moteur de trust_score (barème versionné, validation + recompute serveur, aucune règle en dur côté client).
//  42501 = rejet MÉTIER (admin-check ; jeton-nul=28000) ⇒ via _rpcCall SINGLE-SHOT (F3), sûr par ROLLBACK. ⚠️ admin_set_trust_config
//  CRÉE une version + recalcule (NON idempotent) — mais SÛR : single-shot ⇒ AUCUN retry, donc aucune double-version possible
//  (42501=rollback=0 version ; un reject réseau se propage au 2e callback onRejected, jamais ré-essayé).
//  Renvoie le THENABLE {data,error} → l'appelant garde son `.then(onFulfilled,onRejected)` EXACT.
const rpcGetTrustConfig=()=>_rpcCall("get_trust_config");
/** @param {object} weights @param {string} note */
const rpcAdminSetTrustConfig=(weights,note)=>_rpcCall("admin_set_trust_config",{p_weights:weights,p_note:note});
/** @param {number} version */
const rpcAdminActivateTrustConfigVersion=(version)=>_rpcCall("admin_activate_trust_config_version",{p_version:version});
// Boost/quota (BOOST-RPC-1) : grant_listing_boost = octroi d'un boost d'annonce CONSOMMANT du quota — le serveur plafonne
//  (endsAt 7j) ou n'octroie rien si quota atteint, EN SILENCE (pas un code d'erreur, réconcilié par re-lecture de boost_sale).
//  my_boost_quota = lecture du quota restant. 42501 = not_authenticated (uid nul) OU not_owner (métier) ⇒ via _rpcCall SINGLE-SHOT (F3),
//  sûr par ROLLBACK : 42501=aucun octroi (rien committé), donc aucun double-octroi possible. THENABLE {data,error} → les
//  appelants gardent leur `await`/handling EXACT.
/** @param {string} listingId @param {string} kind @param {number} discountPrice @param {number|null} customEnd @param {boolean} availableToday */
const rpcGrantListingBoost=(listingId,kind,discountPrice,customEnd,availableToday)=>_rpcCall("grant_listing_boost",{p_listing_id:listingId,p_kind:kind,p_discount_price:discountPrice,p_custom_end:customEnd,p_available_today:availableToday});
const rpcMyBoostQuota=()=>_rpcCall("my_boost_quota");
// Admin user-management (ADM-RPC-2 : admin_set_emails_enabled / admin_set_user_blocked / admin_set_boost_tier) — écritures
//  admin-only (SECURITY DEFINER, gardées côté serveur). Via _rpcCall ⇒ mêmes garanties : fonction 1-txn ⇒ toute erreur
//  visible client = ROLLBACK total (aucune double-écriture). _rpcCall est SINGLE-SHOT (F3) ; un refus admin (rejet métier)
//  n'est de toute façon pas rejoué. Fire-and-forget côté appelants ; THENABLE préservé → `.then(undefined,onReject)` EXACT.
/** @param {boolean} enabled */
const rpcAdminSetEmailsEnabled=(enabled)=>_rpcCall("admin_set_emails_enabled",{p_enabled:enabled});
/** @param {string} userId @param {boolean} blocked */
const rpcAdminSetUserBlocked=(userId,blocked)=>_rpcCall("admin_set_user_blocked",{p_user_id:userId,p_blocked:blocked});
/** @param {string} userId @param {string} tier */
const rpcAdminSetBoostTier=(userId,tier)=>_rpcCall("admin_set_boost_tier",{p_user_id:userId,p_tier:tier});
// Notifications (NOTIF-RPC-1 : push_notification / push_admin_notification / get_emails_enabled) — livraison inter-users
//  SECURITY DEFINER, best-effort (« ne casse jamais un flux »). Les 2 push portent un `p_dedup_key` ⇒ IDEMPOTENTS serveur
//  (un double-appel = 1 seule notif). Via _rpcCall ⇒ fonctions 1-txn ⇒ rollback total (aucune double-écriture), + dedup en
//   2e filet. get_emails_enabled = lecture admin. THENABLE préservé → `await`/`.then(onF,onReject)` des appelants EXACTS.
/** @param {string} type @param {object} payload @param {string} dedupKey */
const rpcPushAdminNotification=(type,payload,dedupKey)=>_rpcCall("push_admin_notification",{p_type:type,p_payload:payload,p_dedup_key:dedupKey});
/** @param {string} userId @param {string} type @param {object} payload @param {string} dedupKey */
const rpcPushNotification=(userId,type,payload,dedupKey)=>_rpcCall("push_notification",{p_user_id:userId,p_type:type,p_payload:payload,p_dedup_key:dedupKey});
// NOTIF-RPC-2 : FILIGRANE de la cloche — UNE ecriture (`profiles.notices_seen_at`) au lieu d'un
//  UPDATE de masse sur chaque notification non lue. Serveur ⇒ synchronise entre appareils, et
//  source du badge natif (iOS/Android) que le localStorage ne pouvait pas porter.
const rpcMarkNoticesSeen=()=>_rpcCall("mark_notices_seen");
const rpcGetEmailsEnabled=()=>_rpcCall("get_emails_enabled");
// Préférences utilisateur (PREF-RPC-1 : set_notif_pref / set_timezone) — écritures de RÉGLAGE (SECURITY DEFINER), IDEMPOTENTES
//  par nature (last-write-wins). Via _rpcCall ⇒ fonctions 1-txn ⇒ rollback total. Fire-and-forget ; THENABLE préservé.
/** @param {string} category @param {string} channel @param {boolean} enabled */
const rpcSetNotifPref=(category,channel,enabled)=>_rpcCall("set_notif_pref",{p_category:category,p_channel:channel,p_enabled:enabled});
/** @param {string} tz */
const rpcSetTimezone=(tz)=>_rpcCall("set_timezone",{p_tz:tz});
// Technique (TECH-RPC-1 : touch_presence / increment_listing_views) — écritures 1-txn ⇒ rollback total ; single-shot (F3).
//  touch_presence : heartbeat HF ; l'appelant PRÉ-VÉRIFIE déjà la session (Fix B egress) et un beat manqué s'auto-répare au
//  suivant. increment_listing_views : compteur (42501 ⇒ aucun incrément, rollback). Fire-and-forget ; THENABLE préservé.
const rpcTouchPresence=()=>_rpcCall("touch_presence");
/** @param {string} listingId */
const rpcIncrementListingViews=(listingId)=>_rpcCall("increment_listing_views",{p_listing_id:listingId});
/** EGRESS P1c — recherche de membres SERVEUR (annuaire paresseux). Plafond interne 50, exclut
 *  bloqués et soi ; déployée depuis juillet, branchée ici pour la première fois (contrat 1a).
 *  @param {string} q @param {string} lang @param {number} [limit] */
const rpcMemberSearch=(q,lang,limit)=>_rpcCall("member_search",{p_q:q,p_lang:lang,p_limit:limit||12});
const INBOX_RECENT_MESSAGE_LIMIT=30;
const INBOX_MESSAGE_PAGE_SIZE=500;
const INBOX_CONVO_ID_CHUNK_SIZE=50; // Augmenté de 10 → 50 : réduit les appels API séquentiels au chargement de l'inbox (ex: 30 convos = 1 appel au lieu de 3)
const INBOX_CONVO_SELECT="id,participants,ref_id,ref_title,ref_cat,created_at,updated_at,ref_seller_name,blocked_by";
// ── M4 · REPLIEMENT DE CASSE ET D'ACCENTS ────────────────────────────────────────
//  Le filtre LOCAL de l'inbox comparait en `toLowerCase()` seul : « josé » ne trouvait pas
//  « Jose ». Le serveur, lui, replie les accents des DEUX côtés (`immutable_unaccent`).
//  ⚠️ SANS cet alignement, M4 serait INCOHÉRENT : le serveur ramènerait la conversation, et
//  le filtre local la re-masquerait aussitôt — pire que de ne rien avoir fait.
//  NFD + suppression des diacritiques : c'est le pendant JS d'`unaccent` pour du latin.
//  ⭐ Le changement est STRICTEMENT PLUS PERMISSIF : il trouve davantage, jamais moins.
//  ⚠️ `\p{Diacritic}` (propriété Unicode, drapeau `u`) et NON une plage de points de code :
//  écrite en clair, une plage de diacritiques est invisible à la relecture et un éditeur
//  peut la normaliser en silence. Testé 8/8 (josé/ÉLÈVE/Müller/Çà/ñ/vide/null).
const lncFold=s=>(s||"").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu,"");
// FEED local-first (egress) — projection CARTE des annonces du feed (échantillon accueil, mes annonces,
//  delta/catch-up). `items` sert les CARTES + le filtre/recherche client + les consommateurs toujours actifs
//  (« Pour vous »/« Près de chez vous »/IA/Messenger) + le score priceComparableScore.
//  ⚠️⚠️ CORRECTION (cartographie 3c, 31/07/2026) : ce commentaire affirmait que « le DÉTAIL ET l'ÉDITION
//  re-fetchent la ligne complète ». C'est vrai pour l'ÉDITION (19-app: startEditItem → select("*")), FAUX
//  pour le DÉTAIL : openMarketDetail/openInlineDetail passaient l'objet de `items` TEL QUEL à DetailView,
//  qui ne fetche rien. Toute colonne lue par la fiche était donc INDISPENSABLE ici. L'hydratation à
//  l'ouverture (19-app, EGRESS P3c) rend enfin ce commentaire vrai — et rend la coupe possible. On peut
//  DROPPER 19 colonnes detail-only VÉRIFIÉES non lues par carte/filtre/recherche/score : bathrooms, wc, garage,
//  floor, cellar, balcony, garden, deposit, urgent, video_path/bucket/thumb/uploaded_at/purge_at/closed_at,
//  boosted_until, photos_purge_at, photos_thinned, finalized_at. Coupe ~20-25 % l'egress feed sans régression.
//  (04/10, A17 lot B) + baisse (posée par la base) ; − owner_trust_boost (colonne MORTE ici : zéro lecteur client, elle ne sert
//   qu'au tri serveur) ⇒ la ligne s'allège à l'arrivée de la baisse, mesuré sur les vraies lignes.
const FEED_LISTING_SELECT="id,cat_id,ad_type,prop_type,title,description,city,country,price,status,owner_id,img,lat,lng,ts,area,rooms,furnished,parking,charges,brand,model,year,mileage,fuel,condition,other_cat,created_at,reservation,photos,favorites_count,views,boost_sale,updated_at,work_type,other_sub_cat,car_type,address,seller_display_name,timezone,video_url,baisse";

// ── EGRESS P3c · PROJECTION DE L'ÉCHANTILLON D'ACCUEIL (drapeau `pg_lean_sample`) ────────
//  MESURÉ sur les 200 annonces RÉELLES de la prod (octets JSON, colonne par colonne) :
//    projection complète 228,8 Ko · cette projection 153,7 Ko ⇒ **−33 %** du poste le plus
//    lourd du boot. Les 6 colonnes retirées et leur justification MESURÉE :
//    · `description` (11,1 Ko) et `photos` (12,6 Ko) — la carte ne lit que `photos[0]`, et
//      les 89 annonces sans `img` n'ont AUCUNE photo (vérifié) : `img` couvre 100 % des
//      vignettes. La fiche les récupère par l'hydratation à l'ouverture.
//    · `reservation` (28,7 Ko, le PLUS LOURD) — 110 lignes sur 200 en portent une, mais
//      **0 annonce n'est réellement réservée** : ce sont des résidus d'anciennes réservations
//      que l'app ne consulte que si `status` l'exige. `status` reste dans la projection, donc
//      l'échantillon sait qui hydrater ; `hydrateReservations` le fait, borné (0 le plus souvent).
//    · `owner_trust_boost` — colonne MORTE : absente de `listingFromDB`, zéro lecteur.
//    · `timezone` (12 lignes hors défaut) et `created_at` (repli de `ts`, jamais nul : vérifié 0/200).
//  ⚠️ GARDÉS malgré les apparences (pièges de la cartographie) : `address` (rendu SUR la carte
//    ET dans les haystacks), `model`/`condition` (haystacks seuls), `ts` (TOUS les tris),
//    `video_url` (départage de tri), `updated_at` (pivot de la sonde warm-delta), et TOUS les
//    booléens (`furnished`/`parking`) — le repli hors-ligne de l'édition les remettrait à false.
//  ⚠️ MES annonces gardent la projection COMPLÈTE : elles alimentent le repli d'édition.
//  (04/10, A17 lot B) LA BAISSE DANS L'ÉCHANTILLON, SANS UN OCTET DE PLUS : « b:baisse » (+9 o par ligne, presque toujours null)
//   est compensé par « sdn:seller_display_name » (−16 o par ligne). Les deux ALIAS ne vivent qu'ici : useFeedState les rend à
//   leurs vrais noms (lnaRenommerEchantillon) AVANT listingFromDB — le mappeur et son banc ne lisent que des noms de colonnes.
const FEED_LISTING_SAMPLE_SELECT="id,cat_id,ad_type,prop_type,title,city,country,price,status,owner_id,img,lat,lng,ts,area,rooms,furnished,parking,charges,brand,model,year,mileage,fuel,condition,other_cat,favorites_count,views,boost_sale,updated_at,work_type,other_sub_cat,car_type,address,sdn:seller_display_name,video_url,b:baisse";
//  Les alias de l'échantillon, rendus à leurs vrais noms (seul lecteur : useFeedState, _mapSample). Une ligne sans alias passe telle quelle.
const lnaRenommerEchantillon=row=>{
  if(!row||typeof row!=="object"||!("b" in row||"sdn" in row)) return row;
  const {b,sdn,...reste}=row;
  return {...reste,baisse:b??null,seller_display_name:sdn??null};
};
// Interrupteur (même idiome que pg_profiles_lazy / pg_lean_social). OFF = projection complète,
//  comportement octet pour octet identique. L'hydratation à l'ouverture, elle, est INCONDITIONNELLE
//  (elle ne coûte rien quand l'objet est déjà complet : elle ne part que sur un objet marqué maigre).
const lanacLeanSampleOn=()=>{try{
  const local=localStorage.getItem("lanac:leanSample");
  if(local==="1") return true;
  if(local==="0") return false;
  return localStorage.getItem("lanac:leanSampleServer")==="1";
}catch(e){return false;}};// Hydratation delta des aperçus inbox (2026-07-10) : 1 SEUL message par conversation (limit par parent),
// parent réduit à `id`, pas de reply_to/ref_photo — le strict nécessaire pour l'aperçu + le tri par activité.
const INBOX_PREVIEW_SELECT="id,messages(id,convo_id,from_id,text,ts,seq,kind,status,data,image,system)"; // M3d : seq requis (aperçu = dernier par seq)
//  (lot D · D6-b, 31/10/2026) `read` n'est plus lu par un membre : le filigrane (read_seq) dit le lu, semé du cache dès le boot. MESURÉ : ajouter
//   ici removed_at/updated_at (une projection unique) AUGMENTAIT l'aperçu de 6,6 % — l'aperçu et l'inbox gardent leurs colonnes, moins `read`.
//   L'ADMINISTRATION garde `read` (revue AVANT m5) : elle n'a pas de filigrane (aucune lecture de conversation_participants) — son badge en dépend.
const INBOX_PREVIEW_SELECT_ADMIN="id,messages(id,convo_id,from_id,text,ts,seq,read,kind,status,data,image,system)";
//  Les lectures COMPLÈTES de messages (la réouverture d'un fil, le rattrapage d'un trou, la carte relue) : exactement ce que messageFromDB lit,
//   + updated_at (le curseur du delta) — plus de `select("*")` (offer_amount, moderation, operation_key, created_at, read : ~8 % d'une ligne).
const MESSAGE_COLS="id,convo_id,from_id,text,ts,seq,kind,status,data,image,reply_to,system,removed_at,updated_at";
const messageColsPour=admin=>admin?MESSAGE_COLS+",read":MESSAGE_COLS;
// M5b — WATERMARK de lecture (gardé par pg_read_watermark). lanacReadSeqMap = {convoId -> MON read_seq},
// peuplé au chargement de l'inbox (19-app) + mis à jour à l'ouverture d'un fil (M5c). lanacReadWatermark
// (lot D · D5-c, 30/10/2026) TOUJOURS VRAI — le drapeau pg_read_watermark est allumé depuis le 15/07/2026 ; il n'est plus lu (ni le miroir local,
//  ni app_flags) : un appareil neuf (sans miroir) ou dont la lecture des drapeaux échouait prenait encore le chemin d'avant (`messages.read`, que
//  plus rien n'écrit). Ce n'est plus un coupe-circuit : l'éteindre ferait paraître tous les fils non lus. Les branches « éteint » qui restent
//  (l'insertion sans seq, le compte d'avant, le « Vu » par `read`) sont mortes, nommées pour D6.
const lanacReadWatermark=true;
const lanacReadSeqMap=new Map();
//  (lot D · D6-b, 31/10/2026, revues AVANT m4 / APRÈS n°3) SEMER le filigrane depuis les fils du cache (leur myReadSeq) : un boot depuis le cache sait
//   déjà ce qui est lu. Clés en chaîne (comme partout : convo_id, actId) ; jamais en recul (max) ; une valeur absente, non finie ou négative est
//   ignorée. La réponse serveur de l'inbox reste un `set` (la vérité) : un cache EN AVANCE (lecture optimiste échouée) est corrigé par elle.
const lanacSemerFiligraneDuCache=liste=>{
  (Array.isArray(liste)?liste:[]).forEach(c=>{
    if(!c||c.id==null||c.myReadSeq==null) return;
    const s=Number(c.myReadSeq); if(!Number.isFinite(s)||s<0) return;
    const k=String(c.id); lanacReadSeqMap.set(k,Math.max(Number(lanacReadSeqMap.get(k)||0),s));
  });
};
// Non-lu d'un message pour userId. typeof-guard : extraite ISOLÉMENT par les tests (vars → globalThis) →
// défaut = ancien prédicat `!m.read` (ZÉRO régression, OFF ou fil/seq inconnus). À ON + fil connu (read_seq
// chargé) + seq présent : non-lu SSI seq > mon read_seq. from===userId → jamais non-lu (court-circuit).
const messageUnreadForUser=(m,userId)=>{
  if(!(m&&userId&&m.from!==userId)) return false;
  if(typeof lanacReadWatermark!=="undefined"&&lanacReadWatermark
     &&typeof lanacReadSeqMap!=="undefined"&&lanacReadSeqMap&&m.convoId!=null&&lanacReadSeqMap.has(m.convoId)
     &&m.seq!=null&&Number.isFinite(Number(m.seq))){
    return Number(m.seq)>Number(lanacReadSeqMap.get(m.convoId));
  }
  return !m.read;
};
const convoMessageList=c=>Array.isArray(c?.messages)?c.messages:[];
// --- Cache local des conversations (démarrage instantané, additif, jamais source de vérité) ---
const CACHE_CONVOS_KEY=uid=>"lanac:cache:convos:"+uid;
// MSG-P1 (sync-core, ARMÉ PAR DÉFAUT depuis B0 — kill-switch lanac:inboxShellDelta="0") — cache LÉGER des COQUILLES d'inbox (toutes les
// conversations, sans messages), écrit à chaque loadConvos armé. Le prochain boot ne re-télécharge que les
// coquilles dont `updated_at` a avancé (delta ⊆ full → egress jamais ↑) ; les inchangées viennent du cache.
const CACHE_CONVOSHELLS_KEY=uid=>"lanac:cache:convoshells:"+uid;
// Side-car d'aperçus (2026-07-10) : 1 DERNIER message par conversation, pour TOUTES les conversations (le cache
// principal n'en garde que 30) → au boot suivant, les aperçus/tri se restaurent en LOCAL et l'hydratation delta
// ne télécharge que les conversations réellement mises à jour (egress ≈ 0 à cache chaud, tier Supabase gratuit).
const CACHE_PREVIEWS_KEY=uid=>"lanac:cache:previews:"+uid;
// EGRESS B0 (01/08/2026) · Curseur de synchro des aperçus — LA PIÈCE QUI MANQUAIT AU DELTA.
//  Mesuré en prod : 43 conversations sur 139 étaient « périmées POUR TOUJOURS » — leur
//  `updated_at` (bougé par TOUT changement : lecture, drapeau, deal) dépassait le ts du
//  dernier message de plus de 60 s, or la fraîcheur comparait CES DEUX horloges. Résultat :
//  ~36,6 Ko re-téléchargés à CHAQUE boot chaud, pour rien (le re-téléchargement ne corrige
//  rien puisque l'aperçu revient identique). ⭐ RÈGLE : un delta doit se curseurer sur la
//  MÊME horloge que son filtre. Ce side-car mémorise, par conversation, le `updated_at` VU
//  à la dernière synchro RÉUSSIE : un bump non-message re-télécharge UNE fois, puis se tait.
//  Kill-switch : lanac:previewSync="0" (même idiome que lanac:inboxShellDelta).
const CACHE_PREVIEWSYNC_KEY=uid=>"lanac:cache:previewsync:"+uid;
const lanacPreviewSyncOn=()=>{try{return localStorage.getItem("lanac:previewSync")!=="0";}catch(e){return true;}};
// EGRESS B1 (01/08/2026) · « Près de chez vous » SERVI PAR LE SERVEUR — levier n°3 du contrat 3a.
//  Le défaut d'ÉCHELLE corrigé : la page proximité filtrait l'échantillon top-200 — une annonce
//  à 2 km mais hors top-200 était INVISIBLE (même famille que openListingFromMessage, corrigé
//  en 3a). `listings_feed` porte p_origin/p_radius/p_sort='distance' depuis WS8 : la page
//  demande désormais au serveur les plus PROCHES (borné, trié par distance), au lieu de
//  chercher « proche » dans « récent ». Repli local INFAILLIBLE (RPC en échec ⇒ échantillon,
//  comportement historique). Kill-switch : lanac:nearbyServer="0".
const lanacNearbyServerOn=()=>{try{return localStorage.getItem("lanac:nearbyServer")!=="0";}catch(e){return true;}};
// ── EGRESS B5 (01/08/2026) · LE LOQUET DE PREMIÈRE VISITE ────────────────────────────────
//  LE DÉFAUT MESURÉ (banc 5k, session B3) : les drapeaux serveur s'apprennent au boot N et
//  s'appliquent au boot N+1 — donc la PREMIÈRE visite d'un appareil paie le plein tarif :
//  1 138,4 Ko dont l'annuaire COMPLET (734,9 Ko), soit ~7,3 Mo par nouvel appareil à 50 000
//  membres. Or les trois gros consommateurs (annuaire, follows, reviews) sont des synchros
//  d'ARRIÈRE-PLAN : rien ne les oblige à partir avant la réponse `app_flags` (~1 ligne).
//  ⭐ LE CONTRAT DU LOQUET, trois propriétés :
//   1. un appareil qui CONNAÎT son miroir ne paie RIEN (résolution synchrone immédiate) —
//      le chemin de tous les boots ordinaires est inchangé au cycle près ;
//   2. une PREMIÈRE visite attend la réponse des drapeaux — qui part en tête de boot et
//      pèse une ligne — avant d'évaluer sa porte ;
//   3. FAIL-OPEN : réponse absente à 2 000 ms, erreur, client nul ⇒ on relâche et le
//      comportement redevient EXACTEMENT celui d'avant (les portes lisent l'état connu).
//      Le loquet ne peut donc que RETARDER un gaspillage, jamais bloquer une donnée.
//  ⚠️ CE QU'IL NE GOUVERNE PAS (assumé, doctrine 3b « jamais de changement du chemin de BOOT
//  sans gain mesuré ») : l'échantillon d'accueil (chemin CRITIQUE du premier rendu) garde
//  son comportement — le résiduel première-visite de sa projection (~62 Ko) est documenté.
//  État de module : latch GLOBAL de boot (les drapeaux serveur sont les mêmes pour tous les
//  comptes) — exempt de purge par changement d'utilisateur, cf. per-user-state-guard.
const lanacFlagsLatch={resolu:false,resolve:null,promise:null};
lanacFlagsLatch.promise=new Promise(r=>{lanacFlagsLatch.resolve=r;});
const lanacSignalFlagsReady=()=>{if(!lanacFlagsLatch.resolu){lanacFlagsLatch.resolu=true;try{lanacFlagsLatch.resolve();}catch(e){}}};
const lanacFlagsFirstBootWait=(mirrorKey)=>{
  try{
    if(localStorage.getItem(mirrorKey)!==null) return Promise.resolve();   // appareil déjà instruit : zéro attente
  }catch(e){return Promise.resolve();}                                     // localStorage indisponible ⇒ fail-open
  if(lanacFlagsLatch.resolu) return Promise.resolve();
  return Promise.race([lanacFlagsLatch.promise,new Promise(r=>setTimeout(r,2000))]);
};
const CACHE_MAX_PREVIEWS=400;
const CACHE_MAX_CONVOS=30, CACHE_MAX_CACHED_MSGS=25;
// --- Sync delta listings/profils (additif) : ne re-télécharge que les lignes modifiées depuis la
//     dernière sync, + réconcilie les suppressions via la liste des ids. Borne l'egress à O(changements)
//     quelle que soit la taille de la table. Repli plein automatique si pas de cache (= ancien comportement).
const CACHE_ITEMS_KEY="lanac:items", CACHE_ITEMS_SYNC_KEY="lanac:items:sync", CACHE_USERS_CACHE_KEY="lanac:users", CACHE_USERS_SYNC_KEY="lanac:users:sync";
// Récupère les lignes modifiées depuis lastSync (ou tout si lastSync absent), et la liste des ids
// existants (pour purger les suppressions, uniquement en mode delta). Renvoie null en cas d'erreur
// réseau → l'appelant conserve alors l'état/cache courant (jamais d'écrasement destructif).
async function fetchTableDelta(client,table,selectCols,lastSync){
  const full=!lastSync;
  // ⚠️ PAGINATION OBLIGATOIRE (audit 28/08) : PostgREST plafonne chaque réponse à max-rows
  //  (1 000 par défaut). Sans pagination, un annuaire >1 000 lignes était TRONQUÉ en silence
  //  et la réconciliation delta PURGEAIT du cache tout ce qui dépassait — à chaque boot admin.
  //  Ordre stable par id (sans ordre, l'offset peut sauter/dupliquer des lignes) ; borne dure
  //  MAX_PAGES : au-delà, l'instrument REFUSE plutôt que de mentir (null ⇒ l'appelant garde
  //  son état courant, jamais d'annuaire troué — même contrat d'abstention qu'avant).
  const PAGE=1000,MAX_PAGES=30;
  const pagine=async(cols,decorer)=>{
    const acc=[];
    for(let p=0;p<MAX_PAGES;p++){
      let q=client.from(table).select(cols).order("id",{ascending:true}).range(p*PAGE,(p+1)*PAGE-1);
      if(decorer)q=decorer(q);
      const {data,error}=await q;
      if(error||!Array.isArray(data))return null;
      acc.push(...data);
      if(data.length<PAGE)return acc;
    }
    console.warn(`[lanac] fetchTableDelta(${table}) : ${MAX_PAGES*PAGE} lignes atteintes — sync refusée (borne de l'instrument)`);
    return null;
  };
  const data=await pagine(selectCols,full?null:(q)=>q.gt("updated_at",lastSync)); // strictement supérieur au curseur → pas de re-fetch des lignes déjà vues
  if(!data) return null;
  // Curseur = la PLUS GRANDE valeur updated_at brute renvoyée par PostgREST (précision microseconde
  // préservée en conservant la chaîne d'origine ; comparaison ms puis lexicographique pour départager
  // la même milliseconde). Évite tout re-téléchargement permanent des lignes à la frontière.
  let maxRaw=full?"":(lastSync||"");
  data.forEach(r=>{const u=r&&r.updated_at;if(!u)return;if(!maxRaw){maxRaw=u;return;}const a=Date.parse(u),b=Date.parse(maxRaw);if(a>b||(a===b&&u>maxRaw))maxRaw=u;});
  let allIds=null;
  if(!full){
    const ids=await pagine("id",null);
    if(!ids) return null; // réconciliation indispensable en delta : sans elle on ne peut pas purger → on s'abstient
    allIds=new Set(ids.map(r=>String(r.id)));
  }
  return {rows:data,allIds,maxSync:maxRaw||null,full};
}
// Cache : on ne strippe que les DATA-URL (trop lourdes pour localStorage) — une URL https
// déjà uploadée est légère et doit SURVIVRE au rechargement (l'outbox peut alors rejouer
// un envoi photo COMPLET). ⭐ 28/08 (audit) : quand on strippe l'aperçu d'un envoi NON
// ABOUTI (failed/pending), on pose `imageLost` — l'outbox EXCLUT ces messages plutôt que
// de les renvoyer amputés de leur photo en silence (ils restent barrés, au geste).
const cacheStripMsg=m0=>{
  //  (A42) DANS LE CACHE, un message EN RELANCE porte aussi `failed` : une version d'AVANT (un retour arrière du déploiement,
  //   un onglet pas encore mis à jour) le lit comme un échec à rejouer — jamais comme un message parti. La version courante
  //   lit `relance` d'abord (la collecte, la rangée du fil).
  const m=(m0&&typeof m0.relance==="string"&&m0.failed!==true)?Object.assign({},m0,{failed:true}):m0;
  if(!m||!m.image||!m.image.url) return m;
  if(String(m.image.url).indexOf("data:")!==0) return m;         // URL légère : conservée
  const nonAbouti=m.failed===true||m.pending===true||typeof m.relance==="string"; // (A42) un message EN RELANCE n'est pas abouti non plus
  return Object.assign({},m,{image:null},nonAbouti?{imageLost:true}:{});
};
const cacheStripPhoto=p=>(typeof p==="string"&&p.indexOf("data:")===0)?null:p; // idem refPhoto data-URL
const trimConvoForCache=c=>({id:c.id,participants:c.participants,refId:c.refId,refTitle:c.refTitle,refCat:c.refCat,refPhoto:cacheStripPhoto(c.refPhoto),refSellerName:c.refSellerName,blockedBy:c.blockedBy,lastMessage:c.lastMessage?cacheStripMsg(c.lastMessage):null,unreadCount:c.unreadCount,otherReadSeq:c.otherReadSeq,myReadSeq:(typeof lanacReadSeqMap!=="undefined"&&lanacReadSeqMap.has(c.id))?lanacReadSeqMap.get(c.id):undefined,messages:(Array.isArray(c.messages)?c.messages:[]).slice(-CACHE_MAX_CACHED_MSGS).map(cacheStripMsg)});
// M3a — ordonnancement par SÉQUENCE (seq monotone serveur), gardé par un drapeau. À OFF (défaut) :
// comparateur historique (ts, id) STRICTEMENT identique → zéro régression. À ON : ordre total par seq
// (fallback ts/id) ; les messages optimistes (sans seq → +∞) trient EN BAS et se rangent à leur place
// dès que le seq arrive (echo serveur M2). `lanacSeqOrdering` est piloté par le drapeau pg_seq_ordering.
let lanacSeqOrdering=(()=>{try{const raw=typeof localStorage!=="undefined"&&localStorage.getItem("lanac_pg_flags");if(raw){const o=JSON.parse(raw);return !!(o&&o.pg_seq_ordering===true);}}catch(_){}return false;})();
// Phase 4B : setters du module propriétaire (un import ESM est en lecture seule).
const setLanacSeqOrdering=(v)=>{lanacSeqOrdering=v;};
const _msgOrder=(a,b)=>{
  // typeof-guard : _msgOrder est aussi évalué isolément par les tests (lanacSeqOrdering hors périmètre) → défaut ts.
  if(typeof lanacSeqOrdering!=="undefined"&&lanacSeqOrdering){
    // seq ABSENT (null/undefined = optimiste) → +Infinity (trie EN BAS). ⚠️ Number(null)===0 : il FAUT
    // le garde `!=null`, sinon un message optimiste (seq null) remonterait EN HAUT du fil (bug attrapé).
    const sa=(a&&a.seq!=null&&Number.isFinite(Number(a.seq)))?Number(a.seq):Infinity;
    const sb=(b&&b.seq!=null&&Number.isFinite(Number(b.seq)))?Number(b.seq):Infinity;
    if(sa!==sb) return sa-sb;
  }
  return (Number(a.ts||0)-Number(b.ts||0))||(String(a.id)<String(b.id)?-1:String(a.id)>String(b.id)?1:0);
};
//  (lot D · D6-b, 31/10/2026, revue AVANT M2) ce qu'une lecture ALLÉGÉE ne porte pas (messageFromDB laisse alors la clé ABSENTE) est repris du
//   message déjà connu — ces champs-là SEULS : un étalement de l'ancien garderait des marques locales périmées (pending, failed, relance).
const CHAMPS_DES_LECTURES_COMPLETES=["replyTo","removed","removedAt"];
const completerDepuisLeConnu=(connu,entrant)=>{
  let out=entrant;
  for(const k of CHAMPS_DES_LECTURES_COMPLETES){
    if(!(k in entrant)&&(k in connu)){ if(out===entrant) out={...entrant}; out[k]=connu[k]; }
  }
  return out;
};
const mergeConvoMessageLists=(current=[],incoming=[])=>{
  if(!incoming?.length) return [...(current||[])].sort(_msgOrder);
  const byId=new Map((current||[]).map(m=>[m.id,m]));
  incoming.forEach(m=>{ if(!m?.id) return; const connu=byId.get(m.id); byId.set(m.id,connu?completerDepuisLeConnu(connu,m):m); });
  return [...byId.values()].sort(_msgOrder);
};
// A3 completeness (levier OCTETS) — machine à états de complétude d'un message : « FULL » (texte complet,
// venu de lanac:convo ou d'un fetch REST) vs « PREVIEW » (texte tronqué, venu de la voie inbox lanac:user slim).
// RÈGLE : un PREVIEW ne dégrade JAMAIS un FULL (on garde le contenu complet, on ne prend que les champs
// serveur-autoritaires status/read/seq/ts) ; sinon l'entrant gagne (comportement historique). Tant qu'aucun
// message PREVIEW n'existe (état actuel, _preview=false partout), c'est un STRICT no-op.
const isMsgUpgrade=(existing,incoming)=>!!existing&&!!existing._preview&&!!incoming&&!incoming._preview; // FULL sur PREVIEW
const reconcileMessageVersion=(existing,incoming)=>{
  if(!existing) return incoming;
  if(incoming&&incoming._preview&&!existing._preview)
    return {...existing, status:incoming.status, read:incoming.read,
            seq:(incoming.seq!=null?incoming.seq:existing.seq), ts:incoming.ts||existing.ts};
  return incoming;
};
const convoLastMessage=c=>{
  const messages=convoMessageList(c);
  // Dernier message = MAX sous _msgOrder (robuste a un tableau NON trie). M3d : _msgOrder est LA source
  // unique d'ordre -> l'apercu inbox suit EXACTEMENT le fil. A drapeau OFF, _msgOrder = (ts, id) -> ce
  // max est identique a l'ancien scan "ts max tie-break id" (zero regression). A ON, max par seq : le
  // dernier affiche = dernier du fil (fini la divergence apercu/fil sous decalage d'horloge) ; un envoi
  // optimiste (seq null -> +inf) compte comme dernier -> l'apercu reagit instantanement a l'envoi.
  let last=null;
  for(const m of messages){
    if(!m) continue;
    if(!last||_msgOrder(m,last)>0) last=m;
  }
  const saved=c?.lastMessage||null;
  if(!saved) return last;
  if(!last) return saved;
  // MÊME message (id égal) → préférer la version CALCULÉE (messages[] est rafraîchi par les updates
  // en place : édition, statut). L'ancien `>=0 → saved` figeait l'aperçu inbox sur le texte d'avant
  // édition (défaut pré-existant attrapé au canari M4).
  if(String(saved.id)===String(last.id)) return last;
  return _msgOrder(saved,last)>=0?saved:last;
};
// M3d — RECENCE d'une conversation (tri des listes, comparaisons de fraicheur delta-sync) : max des ts,
// INDEPENDANT du drapeau seq. On decouple exprès l'ACTIVITE (quand ça a bouge -> ts max, semantique
// historique, une convo ne "coule" jamais) de l'APERCU (quel message montrer -> convoLastMessage, seq-aware).
const convoActivityTs=c=>{
  let mx=Number(c?.lastMessage?.ts||0);
  for(const m of convoMessageList(c)){ const t=Number(m?.ts||0); if(t>mx) mx=t; }
  return mx;
};
const convoUnreadCount=(c,userId)=>{
  const explicit=Number(c?.unreadCount);
  const scanned=convoMessageList(c).filter(m=>messageUnreadForUser(m,userId)).length;
  if(Number.isFinite(explicit)&&explicit>=0) return Math.max(explicit,scanned);
  return scanned;
};
// ── P5-c · COMPTEURS D'INBOX SERVEUR — pré-requis de la PAGINATION ──────────────
//  Trois consommateurs balaient aujourd'hui le tableau `convos` ENTIER (badge de
//  non-lus, plafond de réservations actives, quotas d'annulation). Paginer les
//  rendrait FAUX. On déporte donc l'agrégat côté serveur (RPC `inbox_counters`,
//  auth.uid() seul), rangé dans cet objet de module par `useRealtimeInbox`.
//  ⭐ Lu À LA DEMANDE par les gardes IMPÉRATIVES (appelées au clic, pas au rendu) ⇒
//  aucune réactivité requise, aucun nouveau port. Le badge réactif `totalUnread`
//  passe, lui, par un état React miroir (useMessagingState).
//  ⭐⭐ « JAMAIS PIRE » — chaque consommateur prend max(scan local, valeur serveur).
//  Serveur absent (flag OFF ⇒ jamais écrit, ou RPC en échec ⇒ inchangé) ⇒ scan local
//  SEUL = comportement STRICTEMENT identique à aujourd'hui. On ne peut que gagner en
//  justesse quand la liste sera tronquée, jamais régresser.
const lanacInboxServerCounters={value:null};
// Kill-switch de TOUT le bloc P5-c/d/e — DORMANT par défaut. "1" arme d'un bloc :
//  compteurs serveur + pagination keyset + recherche serveur + lazy-load. Lu À CHAUD
//  (jamais figé en closure) ⇒ un flip prend effet au prochain boot sans redéploiement.
// ── INTERRUPTEUR DE LA PAGINATION D'INBOX — priorité EXPLICITE ────────────────
//  1. `lanac:inboxPaging` = OVERRIDE LOCAL ("1" force ON, "0" force OFF). C'est ce que
//     posent les bancs e2e et la mise au point. Il GAGNE toujours : un banc qui arme le
//     drapeau ne doit pas être annulé par la valeur serveur, en silence.
//  2. `lanac:inboxPagingServer` = miroir de `app_flags.pg_inbox_paging`, écrit au boot par
//     `useRemoteFlags`. C'est le VRAI interrupteur de production.
//  3. Défaut : OFF.
//  ⭐ POURQUOI UN INTERRUPTEUR SERVEUR — la valeur vivait dans le localStorage de chaque
//  appareil : l'allumer voulait dire redéployer, et l'ÉTEINDRE EN URGENCE aussi (build +
//  purge des caches, une heure au mieux). Une ligne en base, révocable en une seconde, est
//  le frein à main qu'une fonctionnalité touchant l'inbox de TOUS mérite avant d'être armée.
//  ⚠️ Effet au boot SUIVANT (comme `pg_seq_ordering` / `pg_read_watermark`) : le miroir
//  localStorage évite d'attendre le réseau au démarrage — aucune course, aucune latence.
const lanacInboxPagingOn=()=>{try{
  const local=localStorage.getItem("lanac:inboxPaging");
  if(local==="1") return true;
  if(local==="0") return false;
  return localStorage.getItem("lanac:inboxPagingServer")==="1";
}catch(e){return false;}};
// ── EGRESS P1c · INTERRUPTEUR DE L'ANNUAIRE PARESSEUX — même idiome, même priorité ────
//  Mesure fondatrice (banc 5k, 30/07/2026) : l'annuaire complet = 734,9 Ko décodés / 68 %
//  du boot / 8 appels, LINÉAIRE avec le nombre de membres. Or tous les consommateurs
//  non-admin sont des lookups PAR ID (contrat 1a) déjà servis par l'hydratation existante.
//  À ON (non-admin) : AUCUNE synchro d'annuaire — hydratation par id seulement, recherche
//  de membres via la RPC serveur, cache `lanac:users` borné. L'ADMIN garde la synchro
//  complète (son écran liste les comptes ; audience bornée au rôle).
//  1. `lanac:profilesLazy` = OVERRIDE LOCAL ("1"/"0") — bancs et mise au point, gagne toujours.
//  2. `lanac:profilesLazyServer` = miroir de `app_flags.pg_profiles_lazy` (useRemoteFlags).
//  3. Défaut : OFF — comportement octet pour octet identique à avant.
const lanacProfilesLazyOn=()=>{try{
  const local=localStorage.getItem("lanac:profilesLazy");
  if(local==="1") return true;
  if(local==="0") return false;
  return localStorage.getItem("lanac:profilesLazyServer")==="1";
}catch(e){return false;}};
// ── EGRESS P2c · INTERRUPTEUR DES LECTURES SOCIALES MAIGRES — même idiome ─────────────
//  Contrat 2a (tools/egress-bench/contrat-social-2a.md) : à ON, `follows` ne charge au boot
//  que le sens SORTANT (les abonnés — non bornés par MA propre activité — arrivent à
//  l'OUVERTURE de la modale), `reviews` passe en projection maigre bornée (le gating
//  « 1 avis/deal » n'a besoin ni du texte ni de plus de 300 lignes), et la note headline
//  d'un vendeur redevient l'AGRÉGAT serveur (rating_count/avg, triggers vérifiés exacts)
//  au lieu d'un recalcul local sur une liste tronquée. Défaut OFF = identique à avant.
const lanacLeanSocialOn=()=>{try{
  const local=localStorage.getItem("lanac:leanSocial");
  if(local==="1") return true;
  if(local==="0") return false;
  return localStorage.getItem("lanac:leanSocialServer")==="1";
}catch(e){return false;}};
// ── P5-d · PAGINATEUR D'INBOX (dormant, même drapeau) ─────────────────────────
//  {hasMore, loadMore} posé par useRealtimeInbox après la page 1, consommé par la
//  sentinelle de ConversationList (IntersectionObserver). Objet de module et non état
//  React : le CONSOMMATEUR (11-messenger, lazy) et le PRODUCTEUR (hook eager) ne
//  partagent aucun parent proche — la re-rendu vient de mergeConvoModels (setConvos),
//  la sentinelle relit la valeur fraîche à ce moment-là.
//  ⚠️ PAR-UTILISATEUR ⇒ purgé dans lanacResetPerUserState (22e garde, invariant 1).
const lanacInboxPager={value:null};
// ── BROUILLONS DE MESSAGE, par conversation ───────────────────────────────────
//  Le composeur est REMONTÉ à chaque changement de fil (`key={actId}`) : son texte était
//  donc perdu dès qu'on regardait une autre conversation. Toutes les messageries modernes
//  le conservent, et son absence se remarque immédiatement.
//  ⭐ 100 % LOCAL : aucune requête, aucun octet d'egress, aucun risque serveur.
//  ⭐ Écrit DEPUIS le composeur (jamais remonté en état React) : `MessageComposerInput` est
//  isolé à dessein pour que taper ne re-rende pas le Messenger (prouvé « +0 rendu » par
//  S33/S34) — un `localStorage.setItem` ne déclenche aucun rendu, l'isolation est préservée.
//  BORNES ASSUMÉES — un brouillon est une COMMODITÉ, pas un stockage :
//   • par-utilisateur (clé suffixée de l'uid) ⇒ aucun débordement d'un compte sur l'autre ;
//   • texte tronqué à LNC_DRAFT_MAX (un roman n'a rien à faire dans un localStorage borné) ;
//   • au plus LNC_DRAFT_KEEP conversations, les plus ANCIENNES évincées ⇒ croissance bornée
//     même après des mois (le quota localStorage est déjà sous tension, cf. cache convos) ;
//   • toute entrée illisible ⇒ on repart d'un objet vide, JAMAIS d'exception : ce code
//     tourne au MONTAGE du composeur, il ne doit pas pouvoir empêcher d'écrire un message.
const LNC_DRAFT_MAX=4000, LNC_DRAFT_KEEP=50;
const lncDraftKey=uid=>"lanac:drafts:"+uid;
const lncDraftsRead=uid=>{try{const o=JSON.parse(localStorage.getItem(lncDraftKey(uid))||"{}");return (o&&typeof o==="object"&&!Array.isArray(o))?o:{};}catch(e){return {};}};
const lncDraftGet=(uid,convoId)=>{if(!uid||!convoId) return "";const e=lncDraftsRead(uid)[String(convoId)];return (e&&typeof e.t==="string")?e.t:"";};
const lncDraftSet=(uid,convoId,texte,now)=>{
  if(!uid||!convoId) return false;
  try{
    const tous=lncDraftsRead(uid), cle=String(convoId), t=(texte||"").slice(0,LNC_DRAFT_MAX);
    if(!t.trim()) delete tous[cle];                      // vider le champ EFFACE le brouillon
    else tous[cle]={t,at:Number(now)||0};
    const cles=Object.keys(tous);
    if(cles.length>LNC_DRAFT_KEEP)                        // éviction des plus ANCIENS
      cles.sort((a,b)=>(Number(tous[a]?.at)||0)-(Number(tous[b]?.at)||0))
          .slice(0,cles.length-LNC_DRAFT_KEEP).forEach(k=>delete tous[k]);
    localStorage.setItem(lncDraftKey(uid),JSON.stringify(tous));
    return true;
  }catch(e){return false;}                                // quota plein → on n'insiste pas
};
// ── M4 · RECHERCHE SERVEUR — le complément, pas le remplacement ────────────────
//  LE PROBLÈME QU'ELLE FERME : dès que la liste est paginée, le filtre LOCAL ne voit que
//  les conversations chargées. Il répondrait « aucun résultat » pour une conversation qui
//  EXISTE — une liste tronquée MENT. C'est pour cela que le drapeau restait OFF.
//  ⭐ FORME CHOISIE : on ne rend PAS un chemin d'affichage parallèle. On rapatrie les
//  coquilles manquantes dans `convos` ; le filtre local existant les trouve alors tout
//  seul. La recherche serveur est un DÉCLENCHEUR D'HYDRATATION, pas une seconde vue —
//  même contrat que la pagination : le serveur COMPLÈTE ce qu'on a, il ne le remplace pas.
//  ⭐ Conséquence : l'affichage reste INSTANTANÉ (le local répond sans attendre le réseau)
//  et se complète en arrière-plan. Aucune attente imposée à l'utilisateur.
//  COÛT BORNÉ : 1 RPC + au plus 1 lecture de coquilles, et UNIQUEMENT pour les ids
//  absents — une recherche qui ne trouve que du déjà-chargé ne coûte AUCUNE 2ᵉ requête.
const searchInboxConvos=(q,idsConnus,limit)=>{
  const client=lanacSupabaseClient();
  const terme=(q||"").trim();
  if(!client||!terme) return Promise.resolve([]);
  return rpcInboxSearch(terme,limit).then(res=>{
    if(res?.error){console.error("[LANAC] inbox_search:",res.error.message||res.error,res.error.code);return [];}
    const connus=new Set((idsConnus||[]).map(String));
    const manquants=[...new Set((res?.data||[]).map(r=>r&&r.convo_id).filter(Boolean).map(String))].filter(id=>!connus.has(id));
    if(!manquants.length) return [];                       // tout est déjà là ⇒ 0 requête de plus
    return client.from("conversations").select(INBOX_CONVO_SELECT).in("id",manquants.slice(0,100))
      .then(r2=>{
        if(r2?.error){console.error("[LANAC] inbox_search shells:",r2.error.message,r2.error.code);return [];}
        return (r2?.data||[]).map(convoFromDB).filter(Boolean);
      },err=>{console.error("[LANAC] inbox_search shells exception:",err?.message||err);return [];});
  },err=>{console.error("[LANAC] inbox_search exception:",err?.message||err);return [];});
};
// Range le jsonb de `inbox_counters` dans l'objet de module. Tolérant : entrée invalide
//  ⇒ null (repli scan local). Chaque champ coercé en entier >= 0 (jamais NaN/undefined).
const setLanacInboxServerCounters=(raw)=>{
  if(!raw||typeof raw!=="object"){lanacInboxServerCounters.value=null;return;}
  const n=(x)=>{const v=Number(x);return Number.isFinite(v)&&v>=0?v:0;};
  lanacInboxServerCounters.value={
    unreadWatermark:n(raw.unread_watermark),   // (lot D · D6-a, 31/10/2026) le compte d'avant (unread_legacy) n'est plus servi : le watermark seul
    activeReservations:n(raw.active_reservations),
    countedReserveCancellations:n(raw.counted_cancellations_30d),
    countedRdvCancellations:n(raw.counted_rdv_cancellations_30d),
    notifUnread:n(raw.notif_unread),   // badge cloche AUTORITATIF (non borne par la pagination)
    noticesSeenAt:n(raw.notices_seen_at), // filigrane SERVEUR (ms epoch) — UN SEUL filigrane
  };
};
// ── P5-a · ARCHIVAGE/MASQUAGE — état de COMPTE, servi par le serveur ───────────
//  {convoId -> {archivedAt, deletedAt}}, peuplé au MÊME appel que le watermark
//  (`conversation_participants`, useRealtimeInbox) ⇒ ZÉRO requête ajoutée.
//  Même motif que `lanacReadSeqMap`. La valeur est ensuite RECOPIÉE sur le modèle de
//  conversation par `normalizeConvoForInbox` : elle voyage donc dans l'état React
//  `convos`, ce qui la rend RÉACTIVE (une Map de module ne re-rendrait rien).
const lanacConvoFlagsMap=new Map();
// ── ÉTAT DE MODULE PAR-UTILISATEUR — purge OBLIGATOIRE au changement de compte ────
//  Les TROIS conteneurs ci-dessus (lanacReadSeqMap, lanacInboxServerCounters,
//  lanacConvoFlagsMap) vivent au niveau MODULE : ils SURVIVENT à un changement de
//  compte, parce que la déconnexion ne recharge PAS la page — `19-app` fait
//  `signOut()` puis `setMe(null)`, le SPA reste monté. Sans purge, l'état du compte
//  PRÉCÉDENT s'applique au suivant.
//  ⚠️⚠️ DÉFAUT RÉEL, PAS THÉORIQUE (audit du 27/07) : `lanacConvoFlagsMap` était bien
//  vidée, mais TROP TARD — dans `fetchInboxConversations`, APRÈS l'aller-retour réseau
//  sur `conversation_participants`. Or l'hydratation du cache (useRealtimeInbox, avant
//  toute requête) passe déjà par `normalizeConvoForInbox`, qui LIT cette carte. Et
//  `trimConvoForCache` ne persiste NI archivedAt NI deletedAt ⇒ pendant la fenêtre, la
//  carte de module est la SEULE source de ces champs. Conséquence, sur un appareil
//  partagé : une conversation à laquelle A et B participent tous les deux, masquée par
//  A, DISPARAÎT de l'inbox de B jusqu'à la réponse serveur, puis revient.
//  ⭐ Le sens est unique — `isConversationDeleted` fait un OU (`11-messenger`) ⇒ la
//  contamination ne peut que MASQUER à tort, jamais révéler. Aucune fuite de contenu
//  d'autrui ; mais cacher son propre fil à quelqu'un reste inacceptable.
//  Les deux autres sont AUJOURD'HUI dormants (écrits seulement drapeau armé) — ils
//  deviendraient le même défaut le jour exact où l'on arme la pagination.
//  ⭐ INVARIANT : tout nouvel état de module portant une donnée PAR-UTILISATEUR se purge
//  ICI. Tenu par `tools/per-user-state-guard.mjs`, pas par la vigilance.
//  ⚠️ NE REMPLACE PAS le `clear()` de `fetchInboxConversations` : celui-là a un AUTRE
//  rôle (la requête des drapeaux est FILTRÉE — un drapeau retiré sur un autre appareil
//  ne renvoie plus de ligne, donc « absent = aucun drapeau » exige un vidage à CHAQUE
//  chargement). Ici on purge au changement de COMPTE. Les deux sont nécessaires.
let lanacPerUserOwner; // undefined = aucun compte encore vu ; null = déconnecté (états distincts)
//  A38 (13/10) : LES RANGS DU FIL du membre (id d'annonce → rang donné par fil_echantillon, sous SA RLS et SON profil) — une mémoire
//  de module, pas une propriété d'objet (un objet d'annonce remplacé garde son rang). ⚠️ PAR-UTILISATEUR ⇒ purgée ici.
const lanacFilRangs=new Map();
const lanacResetPerUserState=userId=>{
  const suivant=userId||null;
  if(lanacPerUserOwner===suivant) return false; // même compte (ou re-rendu) ⇒ NE RIEN FAIRE
  lanacPerUserOwner=suivant;
  lanacConvoFlagsMap.clear();
  lanacReadSeqMap.clear();
  lanacInboxServerCounters.value=null;
  lanacInboxPager.value=null; // P5-d : le curseur de pagination du compte précédent meurt avec lui
  lanacFilRangs.clear(); // A38 : les rangs du fil du compte précédent (son profil, sa RLS) meurent avec lui
  lanacFilsEnCreation.clear(); // D6-f : les fils en création du compte précédent ne retiennent plus aucun canal
  // C7 · T14 : les annonces résolues l'ont été sous la RLS du compte précédent ; une réponse encore en vol est ignorée.
  lanacListingStore.gen+=1;
  lanacListingStore.entries.clear();
  lanacListingStore.inflight.clear();
  // A20 lot 2 : les photos lues pour le plein écran l'ont été sous la RLS du compte précédent.
  lanacPhotosStore.gen+=1;
  lanacPhotosStore.vues.clear();
  lanacPhotosStore.enVol.clear();
  if(lanacListingStore.snapshot.length) listingStorePublish();
  return true;
};
const normalizeConvoForInbox=(c,userId,patch={})=>{
  const messages=mergeConvoMessageLists([],patch.messages||c?.messages||[]);
  const base={...c,...patch,messages};
  const last=convoLastMessage(base);
  const rawCount=base.unreadCount;
  const explicit=rawCount!=null&&rawCount!==undefined?Number(rawCount):null;
  // Priorité : patch EXPLICITE (mise à jour optimiste d'une action) > serveur > existant.
  const _flags=lanacConvoFlagsMap.get(base.id);
  return {
    ...base,
    archivedAt:patch.archivedAt!==undefined?patch.archivedAt:(_flags?_flags.archivedAt:(base.archivedAt??null)),
    deletedAt:patch.deletedAt!==undefined?patch.deletedAt:(_flags?_flags.deletedAt:(base.deletedAt??null)),
    lastMessage:last,
    unreadCount:explicit!=null&&Number.isFinite(explicit)&&explicit>=0?explicit:messages.filter(m=>messageUnreadForUser(m,userId)).length,
    messagesLoaded:!!base.messagesLoaded,
  };
};
// -- Phase 4 mappers: reviews & deals -------------------------------------------
const reviewFromDB=row=>!row?null:({
  id:row.id,
  sellerId:row.seller_id,
  reviewerId:row.reviewer_id,
  rating:row.rating,
  comment:row.comment||"",
  dealId:row.deal_id||null,
  itemId:row.item_id||null,
  ts:Number(row.ts||0)||new Date(row.created_at||0).getTime(),
});
const reviewToDB=r=>({
  id:r.id,
  seller_id:r.sellerId,
  reviewer_id:r.reviewerId,
  rating:r.rating,
  comment:r.comment||null,
  deal_id:r.dealId||null,
  item_id:r.itemId||null,
  ts:r.ts||Date.now(),
});
// Projection MAIGRE des transactions (mesuré le 20/07/2026 : −33,0 %, −13 958 o sur le
//  compte le plus chargé de la base). ⭐ DEUX RAISONS, la seconde plus importante que la 1re :
//  (1) ÉCONOMIE — 9 colonnes retirées, en deux familles à la justification DIFFÉRENTE :
//      • 4 que `dealFromDB` NE MAPPE MÊME PAS (operation_key, source_message_id,
//        visit_confirmed_at, transaction_confirmed_at) : comme le fetch fait
//        `data.map(dealFromDB)`, le mapper est l'UNIQUE porte d'entrée ⇒ les retirer
//        est sûr PAR CONSTRUCTION, aucune analyse d'usage requise ;
//      • 4 mappées mais JAMAIS LUES pour une décision — seulement ÉCRITES localement
//        (dispute_action, archived_reason, last_review_reminder_at) ou totalement
//        absentes du client (last_confirmation_reminder_at, vraisemblablement
//        alimentée par un cron serveur).
//  ⚠️⚠️ `confirmation_reminder_count` est CONSERVÉE alors qu'elle n'est jamais lue —
//  et c'est INTENTIONNEL : `dealFromDB` la coerce en `Number(row.X||0)`, donc une
//  colonne absente devient **0 et non null** ⇒ `dealToDB` (qui teste `!=null`)
//  l'ÉCRIRAIT, ÉCRASANT le vrai compteur par 0 EN BASE à la première réécriture de
//  la transaction. La sûreté « null ⇒ omis » ne couvre QUE les champs mappés en
//  `||null` ou par ternaire. Piège attrapé par le filet AVANT livraison (§3).
//  ⇒ RÈGLE GÉNÉRALE : ne retirer une colonne écrite-seulement QUE si son mapper la
//  rend `null` en cas d'absence. Vérifier le mapper, jamais supposer.
//  (2) ⭐⭐ MONOTONIE PAR CONSTRUCTION — c'est le vrai gain d'architecture : avec
//      `select("*")`, TOUTE colonne ajoutée un jour à `deals` gonflait automatiquement
//      l'egress de CHAQUE démarrage, en silence et sans décision. Une liste EXPLICITE
//      rend l'egress incapable d'augmenter tout seul : il faut désormais un acte
//      délibéré. « Ne jamais augmenter l'egress » devient une propriété du code, pas
//      une intention.
//  ⚠️ SÛRETÉ D'ÉCRITURE VÉRIFIÉE : `dealToDB` n'émet un champ que s'il est NON-NULL
//  (`if(d.X!=null) m.col=d.X`) ⇒ une colonne non téléchargée est OMISE de l'UPDATE,
//  jamais écrasée en base. C'est ce qui rend la famille (2) sûre. Filet :
//  tests/deals-lean-projection.test.mjs (équivalence + non-écrasement + contrôle négatif).
const DEAL_LEAN_SELECT="id,item_id,item_title,seller_id,buyer_id,kind,seller_confirmed,buyer_confirmed,status,ts,created_at,confirmed_at,disputed_at,disputed_by,confirmation_reminder_count,review_reminder_count,review_reminder_sent_at,review_reminder_by_user,archived_at,visit_only";
const dealFromDB=row=>!row?null:({
  id:row.id,
  itemId:row.item_id||null,
  itemTitle:row.item_title||"",
  sellerId:row.seller_id,
  buyerId:row.buyer_id,
  kind:row.kind||"",
  sellerConfirmed:!!row.seller_confirmed,
  buyerConfirmed:!!row.buyer_confirmed,
  status:row.status||"pending",
  ts:Number(row.ts||0)||new Date(row.created_at||0).getTime(),
  confirmedAt:row.confirmed_at?Number(row.confirmed_at):null,
  disputedAt:row.disputed_at?Number(row.disputed_at):null,
  disputedBy:row.disputed_by||null,
  disputeAction:row.dispute_action||null,
  confirmationReminderCount:Number(row.confirmation_reminder_count||0),
  lastConfirmationReminderAt:row.last_confirmation_reminder_at?Number(row.last_confirmation_reminder_at):null,
  reviewReminderCount:Number(row.review_reminder_count||0),
  lastReviewReminderAt:row.last_review_reminder_at?Number(row.last_review_reminder_at):null,
  reviewReminderSentAt:row.review_reminder_sent_at?Number(row.review_reminder_sent_at):null,
  reviewReminderByUser:row.review_reminder_by_user||null,
  archivedAt:row.archived_at?Number(row.archived_at):null,
  archivedReason:row.archived_reason||null,
  visitOnly:!!row.visit_only,
});
const dealToDB=d=>{
  const m={
    seller_id:d.sellerId,buyer_id:d.buyerId,
    kind:d.kind||"",
    seller_confirmed:!!d.sellerConfirmed,buyer_confirmed:!!d.buyerConfirmed,
    status:d.status||"pending",
    ts:d.ts||Date.now(),
  };
  if(d.itemId!=null)m.item_id=d.itemId;
  if(d.itemTitle!=null)m.item_title=d.itemTitle;
  if(d.confirmedAt!=null)m.confirmed_at=d.confirmedAt;
  if(d.disputedAt!=null)m.disputed_at=d.disputedAt;
  if(d.disputedBy!=null)m.disputed_by=d.disputedBy;
  if(d.disputeAction!=null)m.dispute_action=d.disputeAction;
  if(d.confirmationReminderCount!=null)m.confirmation_reminder_count=d.confirmationReminderCount;
  if(d.lastConfirmationReminderAt!=null)m.last_confirmation_reminder_at=d.lastConfirmationReminderAt;
  if(d.reviewReminderCount!=null)m.review_reminder_count=d.reviewReminderCount;
  if(d.lastReviewReminderAt!=null)m.last_review_reminder_at=d.lastReviewReminderAt;
  if(d.reviewReminderSentAt!=null)m.review_reminder_sent_at=d.reviewReminderSentAt;
  if(d.reviewReminderByUser!=null)m.review_reminder_by_user=d.reviewReminderByUser;
  if(d.archivedAt!=null)m.archived_at=d.archivedAt;
  if(d.archivedReason!=null)m.archived_reason=d.archivedReason;
  if(d.visitOnly!=null)m.visit_only=d.visitOnly;
  if(d.operationKey!=null)m.operation_key=d.operationKey; // Audit 2026-07-04 (Lot C) : idempotence deals réservation
  return m;
};
// Projection MAIGRE des signalements pour un NON-ADMIN (mesuré le 20/07/2026 : −40,5 %,
//  −20 149 o sur un compte réel de 64 lignes ⇒ ~−9 % du budget d'egress du boot).
//  Même motif que FEED_LISTING_SELECT (Feed-P1b) : on ne télécharge que ce qu'on LIT.
//  ⚠️ CHAQUE colonne ci-dessous est justifiée par un lecteur NON-ADMIN — ne pas en
//  retirer sans re-vérifier son lecteur, une colonne absente devient `null` EN SILENCE
//  (reportFromDB a un défaut pour chaque champ) et casserait la logique sans erreur :
//    • status,type,item_id,reporter_id,owner_id → dealSuspendedByOpenIncident (useTransactionActions) : seul un
//      INCIDENT ouvert entre les deux parties suspend les tâches d'un accord (C7 · T5, jumeau du prédicat serveur)
//    • reason,details,ts,created_at,owner_id,status → trustBreakdown / userIncidentReports (repli local de la
//      confiance ; created_at est le repli de ts dans reportFromDB)
//    • id → identité de ligne (reportMatch, dédup)
//    • ⭐ source_message_id → gardes ANTI-DOUBLON d'incident (useTransactionActions
//      L803/L819/L971/L1143) ; absente, la garde conclut « pas de doublon » et
//      RE-CRÉERAIT un signalement à chaque action. Piège attrapé à l'audit du 20/07.
//    • ⭐ convo_id → même garde pour les signalements de CONVERSATION (19-app L1078/L1080)
//    • deal_id → FK structurelle vers la transaction (conservée à dessein : 2 % du poids)
//  Colonnes ÉCARTÉES (affichage/workflow ADMIN uniquement, l'admin reçoit `*`) :
//    item_title, reporter_name (AdminView),
//    disputed_at/by, resolved_at/by, final_result, acknowledged_at/by (frise admin).
const REPORT_LEAN_SELECT="id,type,item_id,reporter_id,owner_id,reason,details,status,ts,created_at,source_message_id,convo_id,deal_id";
// (14/09, C7 · T5) LE PRÉDICAT UNIQUE, jumeau de public.deal_suspended_by_open_incident (migration
//  personne_ne_gele_la_transaction_des_autres) : les tâches d'un accord ne sont suspendues que pendant un INCIDENT
//  (réservation, visite, transaction) OUVERT sur SON annonce, dont le plaignant et la personne mise en cause sont SES
//  DEUX PARTIES. Un signalement — d'un tiers ou des parties —, une sanction, l'incident d'un tiers : rien n'est suspendu.
const INCIDENT_REPORT_TYPES=new Set(["reservation","visit","transaction"]);
const dealSuspendedByOpenIncident=(deal,reports=[])=>!!deal&&deal.itemId!=null&&(reports||[]).some(r=>
  r.status==="open"&&INCIDENT_REPORT_TYPES.has(r.type)&&r.itemId!=null&&String(r.itemId)===String(deal.itemId)&&
  ((r.reporterId===deal.sellerId&&r.ownerId===deal.buyerId)||(r.reporterId===deal.buyerId&&r.ownerId===deal.sellerId))
);
const reportFromDB=row=>!row?null:({
  id:row.id,type:row.type||null,
  itemId:row.item_id||null,itemTitle:row.item_title||"",
  reporterId:row.reporter_id,reporterName:row.reporter_name||"",
  ownerId:row.owner_id||null,reportedUserId:row.owner_id||null,
  reporterUserId:row.reporter_id||null,
  convoId:row.convo_id||null,conversationId:row.convo_id||null,
  reason:row.reason||"",details:row.details||null,
  status:row.status||"open",
  sourceMessageId:row.source_message_id||null,
  dealId:row.deal_id||null,
  disputedAt:row.disputed_at?Number(row.disputed_at):null,disputedBy:row.disputed_by||null,
  resolvedAt:row.resolved_at?Number(row.resolved_at):null,resolvedBy:row.resolved_by||null,
  finalResult:row.final_result||null,
  acknowledgedAt:row.acknowledged_at?Number(row.acknowledged_at):null,acknowledgedBy:row.acknowledged_by||null,
  ts:Number(row.ts||0)||new Date(row.created_at||0).getTime(),
});
const reportToDB=r=>({
  type:r.type||null,item_id:r.itemId||null,item_title:r.itemTitle||null,
  reporter_id:r.reporterId,reporter_name:r.reporterName||"",
  owner_id:r.ownerId||r.reportedUserId||null,convo_id:r.convoId||r.conversationId||null,
  reason:r.reason||"",details:r.details||null,status:r.status||"open",
  source_message_id:r.sourceMessageId||null,deal_id:r.dealId||null,
  disputed_at:r.disputedAt||null,disputed_by:r.disputedBy||null,
  resolved_at:r.resolvedAt||null,resolved_by:r.resolvedBy||null,
  final_result:r.finalResult||null,acknowledged_at:r.acknowledgedAt||null,
  acknowledged_by:r.acknowledgedBy||null,ts:r.ts||Date.now(),
});
// -- Phase 5C mappers: alerts --------------------------------------------------
const alertFromDB=row=>!row?null:({
  id:row.id,
  label:row.label||"",
  cat:row.cat||null,
  adType:row.ad_type||null,
  other:row.other||null,
  country:row.country||"all",
  city:row.city||"all",
  radius:Number(row.radius||0),
  coords:(row.coords&&Number.isFinite(Number(row.coords.lat))&&Number.isFinite(Number(row.coords.lng)))?{lat:Number(row.coords.lat),lng:Number(row.coords.lng)}:null,
  max:row.max_price!=null?Number(row.max_price):5000000,
  query:row.query||null,
  ts:Number(row.ts||0)||new Date(row.created_at||0).getTime(),
});
const alertToDB=a=>({
  label:a.label||"",
  cat:a.cat||null,
  ad_type:a.adType||null,
  other:a.other||null,
  country:a.country||"all",
  city:a.city||"all",
  radius:Number(a.radius||0),
  coords:(a.coords&&Number.isFinite(a.coords.lat)&&Number.isFinite(a.coords.lng))?{lat:a.coords.lat,lng:a.coords.lng}:null,
  max_price:a.max!=null?Number(a.max):null,
  query:a.query||null,
  ts:a.ts||Date.now(),
});
//  (04/10, A17) L'ALERTE est la recherche ENREGISTRÉE, toute entière : elle garde aussi les six sous-filtres que le marché applique
//   (le serveur les applique à la publication : alerte_correspond). Convertisseurs PROPRES aux alertes — alertFromDB/alertToDB
//   servent aussi l'historique de recherche, dont la base rend et reçoit EXACTEMENT ses douze colonnes (banc B3).
const alerteFromDB=row=>!row?null:({...alertFromDB(row),
  otherSub:row.other_sub||"",propType:row.prop_type||"",rooms:row.rooms!=null?Number(row.rooms):null,
  brand:row.brand||"",fuel:row.fuel||"",workType:row.work_type||"",
  //  (13/10, A40) ce que la base tient pour la feuille « Mes alertes » : la pause, le compteur de nouveautés (jamais négatif), la date de la derniere
  enPause:!!row.en_pause,nouveautes:Math.max(0,Number(row.nouveautes)||0),derniereAt:row.derniere_at?(Date.parse(row.derniere_at)||null):null});
const alerteToDB=a=>({...alertToDB(a),
  other_sub:a.otherSub||null,prop_type:a.propType||null,
  rooms:(a.rooms!=null&&a.rooms!==""&&Number.isFinite(Number(a.rooms)))?Number(a.rooms):null,
  brand:a.brand||null,fuel:a.fuel||null,work_type:a.workType||null});
//  (04/10, A17) CE QU'UNE NOTICE RICHE GARDE DE SON PAYLOAD — les correspondances d'une alerte (et, au lot B, les baisses de
//   prix) : les VRAIES photos (3 au plus, la plus récente d'abord), le compteur, l'annonce (titre, prix, loyer, ville). Pure,
//   testée. Tout autre type : null — sa ligne reste exactement celle d'aujourd'hui.
const NOTICE_RICHES=new Set(["alert_match","price_drop"]);
const richNoticeFromRow=row=>{
  if(!row||!NOTICE_RICHES.has(row.type)) return null;
  const p=row.payload&&typeof row.payload==="object"?row.payload:{};
  const nb=v=>(v!=null&&v!==""&&Number.isFinite(Number(v)))?Number(v):null;
  const n=nb(p.count);
  return {
    photos:(Array.isArray(p.photos)?p.photos:[]).filter(x=>typeof x==="string"&&x).slice(0,3),
    count:n&&n>0?Math.floor(n):1,
    label:typeof p.label==="string"?p.label:"",
    titre:typeof p.titre==="string"?p.titre:"",
    prix:nb(p.prix),avant:nb(p.avant),
    loyer:p.loyer===true,
    ville:typeof p.ville==="string"?p.ville:"",
    raison:typeof p.raison==="string"?p.raison:"",
  };
};
const validEmail=email=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email||"").trim());
const validPhone=phone=>{
  const clean=(phone||"").trim();
  const digits=clean.replace(/\D/g,"");
  return /^\+?[0-9 ()\-.]{6,}$/.test(clean)&&digits.length>=6&&digits.length<=15;
};

export { lanacFilsEnCreation, noterCreationDuFil, acceptedReservationCard, laPlusRecenteParLaBase, statutDuVoteParLaBase, cleDuGesteSurLeDossier, lirePhotosDesAnnonces, photosDeLAnnonce, PHOTOS_LOT_MAX, alerteFromDB, alerteToDB, richNoticeFromRow, lnaRenommerEchantillon, CACHE_CONVOS_KEY, dealConvoMatch, postToDealConvos, resolveDealConvos, LISTING_RESOLVE_GESTURE_MS, LISTING_RESOLVE_TTL_MS, patchResolvedListings, peekResolvedListing, rememberListings, resolveListing, resolveListingsInBackground, resolvedListingState, resolvedListingsSnapshot, subscribeResolvedListings,CACHE_CONVOSHELLS_KEY, CACHE_ITEMS_KEY, CACHE_ITEMS_SYNC_KEY, CACHE_MAX_CACHED_MSGS, CACHE_MAX_CONVOS, CACHE_MAX_PREVIEWS, CACHE_PREVIEWS_KEY, CACHE_PREVIEWSYNC_KEY, lanacPreviewSyncOn, lanacNearbyServerOn, lanacSignalFlagsReady, lanacFlagsFirstBootWait, CACHE_USERS_CACHE_KEY, CACHE_USERS_SYNC_KEY, FEED_LISTING_SELECT, FEED_LISTING_SAMPLE_SELECT, INBOX_CONVO_ID_CHUNK_SIZE, INBOX_CONVO_SELECT, INBOX_MESSAGE_PAGE_SIZE, INBOX_PREVIEW_SELECT, INBOX_PREVIEW_SELECT_ADMIN, MESSAGE_COLS, messageColsPour, lanacSemerFiligraneDuCache, INBOX_RECENT_MESSAGE_LIMIT, TRUST_AUTOAPPROVE, _msgOrder, alertFromDB, alertToDB, cacheStripMsg, cacheStripPhoto, convoActivityTs, convoFromDB, convoLastMessage, convoMessageList, convoToDB, convoUnreadCount, DEAL_LEAN_SELECT, dealFromDB, dealSuspendedByOpenIncident, dealToDB, fetchTableDelta, insertMessage, isMsgUpgrade, lanacConvoFlagsMap, lanacInboxPagingOn, lanacLeanSampleOn, lanacLeanSocialOn, lanacProfilesLazyOn, lanacInboxServerCounters, lanacReadSeqMap, lanacReadWatermark, lanacResetPerUserState, lanacSeqOrdering, markProfileNudgeSeen, setLanacInboxServerCounters, mergeConvoMessageLists, messageDataToDB, messageFromDB, messageToDB, messageUnreadForUser, messageUpdateToDB, normalizeConvoForInbox, persistConvoAndMessage, etatDeNaissance, persistListingInsert, persistListingUpdate, persistMessagePatch, persistMessageUpdate, profileNudgeSeen, reconcileMessageVersion, rpcAdminActivateTrustConfigVersion, rpcAdminSetBoostTier, rpcAdminSetEmailsEnabled, rpcAdminSetTrustConfig, rpcAdminSetUserBlocked, rpcCastDealVote, rpcCastIncidentResolution, rpcCloseIncidentReport, rpcConfirmDeal, rpcCreateConfirmedDeal, rpcDecideRequest, rpcRefreshMyRequest, rpcWithdrawMyRequest, rpcCancelMyReservation, rpcGetEmailsEnabled, rpcGetTrustConfig, rpcGrantListingBoost, rpcMyBoostQuota, rpcPushAdminNotification, rpcPushNotification, rpcMarkNoticesSeen, rpcIncrementListingViews, rpcMemberSearch, rpcRecordSelfStrike, rpcRecordTrustStrike, rpcSetNotifPref, rpcSetTimezone, rpcTouchPresence, rpcGetPresence, lanacInboxPager, lncDraftGet, lncDraftSet, lncFold, rpcInboxPage, rpcInboxSearch, rpcRemoveMessage, searchInboxConvos, rpcInboxCounters, rpcReprise, rpcFilProfil, lanacFilRangs, rpcMyAccountEngagements, rpcMarkConvoRead, rpcMyFailedOfferCounts, rpcMyUnreadCounts, rpcSetConversationFlags, REPORT_LEAN_SELECT, reportFromDB, reportToDB, reviewFromDB, reviewToDB, setLanacSeqOrdering, trimConvoForCache, trustAutoApproveStatus, validEmail, validPhone };
